import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Briefcase,
    Phone,
    Mail,
    MapPin,
    Clock,
    DollarSign,
    Truck,
    User,
    FileText,
    Mic,
    Upload,
    CheckCircle,
    AlertTriangle,
    XCircle,
    ArrowUp,
    Trash2,
    RefreshCw,
    Loader2,
    Building2,
    Calendar,
    Route,
    Shield,
    ShieldAlert,
    Zap,
    MessageSquare,
    FileCheck,
    BookCheck,
    PenTool,
    Download,
    Maximize2,
    Minimize2,
    TrendingUp,
    ChevronDown,
    Edit,
    Check,
    X,
    Copy,
    Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Format currency
const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(amount || 0);
};

// Calculate ETA based on distance, speed, and HOS
const calculateETA = (distance, hosState, avgSpeed = 55) => {
    if (!distance) return { hours: 0, eta: '--' };
    
    const driveHoursNeeded = distance / avgSpeed;
    const driveHoursRemaining = hosState?.drive_hours_remaining || 11;
    
    let totalHours = driveHoursNeeded;
    if (driveHoursNeeded > driveHoursRemaining) {
        // Need 10hr reset
        const resets = Math.ceil((driveHoursNeeded - driveHoursRemaining) / 11);
        totalHours += resets * 10;
    }
    
    const eta = new Date(Date.now() + totalHours * 60 * 60 * 1000);
    return {
        hours: Math.round(totalHours * 10) / 10,
        eta: eta.toLocaleString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
        })
    };
};

// Safety Info Modal - Quick access to registry numbers during broker calls
function SafetyInfoModal({ truckId, truckName }) {
    const [safetyInfo, setSafetyInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const fetchSafetyInfo = async () => {
        if (!truckId) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/trucks/${truckId}/compliance`, {
                credentials: 'include'
            });
            if (res.ok) {
                const data = await res.json();
                setSafetyInfo(data);
            }
        } catch (e) {
            console.error('Failed to fetch safety info:', e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && !safetyInfo) {
            fetchSafetyInfo();
        }
    }, [isOpen]);

    const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied to clipboard`);
    };

    // Helper to build proper download URL for compliance documents
    const getDocumentUrl = (docPath) => {
        if (!docPath) return null;
        // docPath format: "compliance/{truck_id}/{filename}" 
        // Target URL: {API_URL}/api/uploads/{docPath}
        const baseUrl = API_URL || window.location.origin;
        const cleanPath = docPath.startsWith('/') ? docPath.substring(1) : docPath;
        const fullUrl = `${baseUrl}/api/uploads/${cleanPath}`;
        console.log('[SafetyInfoModal] getDocumentUrl:', { docPath, baseUrl, cleanPath, fullUrl });
        return fullUrl;
    };

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    data-testid="safety-info-btn"
                >
                    <Shield className="w-4 h-4 mr-2" />
                    Safety Info
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-white">
                        <Shield className="w-5 h-5 text-blue-500" />
                        Safety Compliance Info
                    </DialogTitle>
                </DialogHeader>
                
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    </div>
                ) : safetyInfo ? (
                    <div className="space-y-4">
                        {/* Compliance Status */}
                        <div className={`p-3 rounded-lg ${safetyInfo.compliant ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                            <div className="flex items-center gap-2">
                                {safetyInfo.compliant ? (
                                    <CheckCircle className="w-5 h-5 text-green-400" />
                                ) : (
                                    <AlertTriangle className="w-5 h-5 text-red-400" />
                                )}
                                <span className={`font-medium ${safetyInfo.compliant ? 'text-green-400' : 'text-red-400'}`}>
                                    {safetyInfo.compliant ? 'DOT Compliant' : 'Non-Compliant'}
                                </span>
                            </div>
                        </div>

                        {/* Quick Reference Numbers - For Broker Calls */}
                        <div className="bg-zinc-800/50 rounded-lg p-3 space-y-3">
                            <h4 className="text-sm font-medium text-white flex items-center gap-2">
                                <Phone className="w-4 h-4 text-green-400" />
                                Quick Reference (Copy for Broker)
                            </h4>
                            
                            {/* National Registry Number */}
                            <div className="flex items-center justify-between p-2 bg-zinc-900/50 rounded">
                                <div>
                                    <p className="text-xs text-zinc-500">National Registry #</p>
                                    <p className="text-white font-mono">
                                        {safetyInfo.compliance_data?.med_registry_num || 'Not on file'}
                                    </p>
                                </div>
                                {safetyInfo.compliance_data?.med_registry_num && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => copyToClipboard(safetyInfo.compliance_data.med_registry_num, 'Registry #')}
                                        className="text-zinc-400 hover:text-white"
                                    >
                                        Copy
                                    </Button>
                                )}
                            </div>

                            {/* CDL Number */}
                            <div className="flex items-center justify-between p-2 bg-zinc-900/50 rounded">
                                <div>
                                    <p className="text-xs text-zinc-500">CDL Number</p>
                                    <p className="text-white font-mono">
                                        {safetyInfo.compliance_data?.cdl_number || 'Not on file'}
                                    </p>
                                </div>
                                {safetyInfo.compliance_data?.cdl_number && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => copyToClipboard(safetyInfo.compliance_data.cdl_number, 'CDL #')}
                                        className="text-zinc-400 hover:text-white"
                                    >
                                        Copy
                                    </Button>
                                )}
                            </div>

                            {/* Medical Examiner */}
                            <div className="flex items-center justify-between p-2 bg-zinc-900/50 rounded">
                                <div>
                                    <p className="text-xs text-zinc-500">Medical Examiner</p>
                                    <p className="text-white">
                                        {safetyInfo.compliance_data?.med_doc_name || 'Not on file'}
                                    </p>
                                </div>
                            </div>

                            {/* Insurance Provider */}
                            <div className="flex items-center justify-between p-2 bg-zinc-900/50 rounded">
                                <div>
                                    <p className="text-xs text-zinc-500">Insurance Provider</p>
                                    <p className="text-white">
                                        {safetyInfo.compliance_data?.coi_provider || 'Not on file'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Compliance Details */}
                        <div className="space-y-2">
                            <h4 className="text-sm font-medium text-zinc-400">Compliance Status</h4>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                <div className="p-2 bg-zinc-800/50 rounded">
                                    <p className="text-zinc-500 text-xs">Medical Exam</p>
                                    <p className="text-white">{safetyInfo.compliance_data?.med_exam_date || 'N/A'}</p>
                                </div>
                                <div className="p-2 bg-zinc-800/50 rounded">
                                    <p className="text-zinc-500 text-xs">Insurance Expiry</p>
                                    <p className="text-white">{safetyInfo.compliance_data?.coi_expiry || 'N/A'}</p>
                                </div>
                                <div className="p-2 bg-zinc-800/50 rounded">
                                    <p className="text-zinc-500 text-xs">MVR Check</p>
                                    <p className="text-white">{safetyInfo.compliance_data?.mvr_check_date || 'N/A'}</p>
                                </div>
                                <div className="p-2 bg-zinc-800/50 rounded">
                                    <p className="text-zinc-500 text-xs">Drug Test</p>
                                    <p className={`capitalize ${safetyInfo.compliance_data?.drug_test_status === 'verified' ? 'text-green-400' : 'text-amber-400'}`}>
                                        {safetyInfo.compliance_data?.drug_test_status || 'Pending'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Compliance Documents - Download for Broker */}
                        <div className="space-y-2">
                            <h4 className="text-sm font-medium text-white flex items-center gap-2">
                                <Download className="w-4 h-4 text-blue-400" />
                                Compliance Documents (Download for Broker)
                            </h4>
                            <div className="space-y-2">
                                {/* Certificate of Insurance */}
                                <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded border border-zinc-700">
                                    <div className="flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-blue-400" />
                                        <div>
                                            <p className="text-white text-sm">Certificate of Insurance (COI)</p>
                                            <p className="text-zinc-500 text-xs">
                                                {safetyInfo.compliance_data?.coi_provider || 'No provider'} 
                                                {safetyInfo.compliance_data?.coi_policy_number && ` • ${safetyInfo.compliance_data.coi_policy_number}`}
                                            </p>
                                        </div>
                                    </div>
                                    {safetyInfo.compliance_data?.coi_doc_url ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => window.open(getDocumentUrl(safetyInfo.compliance_data.coi_doc_url), '_blank')}
                                            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                                            data-testid="download-coi-btn"
                                        >
                                            <Download className="w-4 h-4 mr-1" />
                                            Download
                                        </Button>
                                    ) : (
                                        <span className="text-zinc-500 text-xs">Not uploaded</span>
                                    )}
                                </div>

                                {/* Medical Card */}
                                <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded border border-zinc-700">
                                    <div className="flex items-center gap-2">
                                        <FileCheck className="w-4 h-4 text-green-400" />
                                        <div>
                                            <p className="text-white text-sm">Medical Card</p>
                                            <p className="text-zinc-500 text-xs">
                                                {safetyInfo.compliance_data?.med_doc_name || 'No examiner'} 
                                                {safetyInfo.compliance_data?.med_exam_date && ` • Exam: ${safetyInfo.compliance_data.med_exam_date}`}
                                            </p>
                                        </div>
                                    </div>
                                    {safetyInfo.compliance_data?.med_card_url ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => window.open(getDocumentUrl(safetyInfo.compliance_data.med_card_url), '_blank')}
                                            className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                                            data-testid="download-medical-btn"
                                        >
                                            <Download className="w-4 h-4 mr-1" />
                                            Download
                                        </Button>
                                    ) : (
                                        <span className="text-zinc-500 text-xs">Not uploaded</span>
                                    )}
                                </div>

                                {/* Motor Vehicle Record */}
                                <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded border border-zinc-700">
                                    <div className="flex items-center gap-2">
                                        <Truck className="w-4 h-4 text-amber-400" />
                                        <div>
                                            <p className="text-white text-sm">Motor Vehicle Record (MVR)</p>
                                            <p className="text-zinc-500 text-xs">
                                                {safetyInfo.compliance_data?.mvr_check_date 
                                                    ? `Last check: ${safetyInfo.compliance_data.mvr_check_date}`
                                                    : 'No check date'}
                                            </p>
                                        </div>
                                    </div>
                                    {safetyInfo.compliance_data?.mvr_doc_url ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => window.open(getDocumentUrl(safetyInfo.compliance_data.mvr_doc_url), '_blank')}
                                            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                                            data-testid="download-mvr-btn"
                                        >
                                            <Download className="w-4 h-4 mr-1" />
                                            Download
                                        </Button>
                                    ) : (
                                        <span className="text-zinc-500 text-xs">Not uploaded</span>
                                    )}
                                </div>

                                {/* Drug Test Document */}
                                <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded border border-zinc-700">
                                    <div className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-purple-400" />
                                        <div>
                                            <p className="text-white text-sm">Drug Test Results</p>
                                            <p className="text-zinc-500 text-xs">
                                                Status: <span className={`capitalize ${safetyInfo.compliance_data?.drug_test_status === 'verified' ? 'text-green-400' : 'text-amber-400'}`}>
                                                    {safetyInfo.compliance_data?.drug_test_status || 'pending'}
                                                </span>
                                                {safetyInfo.compliance_data?.drug_test_date && ` • ${safetyInfo.compliance_data.drug_test_date}`}
                                            </p>
                                        </div>
                                    </div>
                                    {safetyInfo.compliance_data?.drug_test_doc_url ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => window.open(getDocumentUrl(safetyInfo.compliance_data.drug_test_doc_url), '_blank')}
                                            className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                            data-testid="download-drug-test-btn"
                                        >
                                            <Download className="w-4 h-4 mr-1" />
                                            Download
                                        </Button>
                                    ) : (
                                        <span className="text-zinc-500 text-xs">Not uploaded</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Non-compliant warnings */}
                        {!safetyInfo.compliant && safetyInfo.failures?.length > 0 && (
                            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                                <h4 className="text-sm font-medium text-red-400 mb-2">Issues Requiring Attention</h4>
                                <ul className="space-y-1">
                                    {safetyInfo.failures.map((f, idx) => (
                                        <li key={idx} className="text-red-400 text-sm flex items-start gap-2">
                                            <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                                            {f.reason}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className="text-zinc-500 text-center py-4">No safety information available</p>
                )}
            </DialogContent>
        </Dialog>
    );
}

// Waiting Load Card (compact)
function WaitingLoadCard({ item, onMoveToTop, onRemove, loading }) {
    const load = item.load || {};
    
    return (
        <div className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700/50" data-testid={`waiting-load-${load.load_id}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Badge variant="outline" className="border-zinc-600 text-zinc-400">
                        #{item.position + 1}
                    </Badge>
                    <div>
                        <span className="text-white font-mono text-sm">{load.load_id?.slice(-8)}</span>
                        <p className="text-zinc-400 text-xs">
                            {load.origin_city}, {load.origin_state} → {load.destination_city}, {load.destination_state}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-green-400 font-semibold">{formatCurrency(load.gross_pay)}</span>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onMoveToTop(load.load_id)}
                        disabled={loading}
                        className="text-zinc-400 hover:text-white"
                        data-testid={`move-top-${load.load_id}`}
                    >
                        <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onRemove(load.load_id)}
                        disabled={loading}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`remove-${load.load_id}`}
                    >
                        <Trash2 className="w-4 h-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

// ==================== NEGOTIATION BRAIN ====================
// Freight University "Freight Researcher" Module

// ==================== DISPATCH DECISION ENGINE ====================
// Phase 3 - Decision-based layer on top of calculations

function DispatchDecisionPanel({ loadId, load }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(true);
    
    useEffect(() => {
        const fetchDecision = async () => {
            if (!loadId) return;
            setLoading(true);
            try {
                const res = await fetch(`${API_URL}/api/operations/dispatch-decision/${loadId}`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    const result = await res.json();
                    setData(result);
                }
            } catch (e) {
                console.error('Failed to fetch dispatch decision:', e);
            } finally {
                setLoading(false);
            }
        };
        
        fetchDecision();
    }, [loadId]);
    
    // Decision styling
    const getDecisionStyle = (decision) => {
        switch (decision) {
            case 'BOOK':
                return {
                    bg: 'bg-green-500/20',
                    border: 'border-green-500/50',
                    text: 'text-green-400',
                    icon: <CheckCircle className="w-8 h-8 text-green-500" />,
                    label: '✅ BOOK'
                };
            case 'NEGOTIATE':
                return {
                    bg: 'bg-amber-500/20',
                    border: 'border-amber-500/50',
                    text: 'text-amber-400',
                    icon: <MessageSquare className="w-8 h-8 text-amber-500" />,
                    label: '💬 NEGOTIATE'
                };
            case 'PASS':
                return {
                    bg: 'bg-red-500/20',
                    border: 'border-red-500/50',
                    text: 'text-red-400',
                    icon: <XCircle className="w-8 h-8 text-red-500" />,
                    label: '🛑 PASS'
                };
            default:
                return {
                    bg: 'bg-zinc-800',
                    border: 'border-zinc-700',
                    text: 'text-zinc-400',
                    icon: <AlertTriangle className="w-8 h-8 text-zinc-500" />,
                    label: '❓ ANALYZING'
                };
        }
    };
    
    // Risk flag styling
    const getRiskStyle = (severity) => {
        switch (severity) {
            case 'high':
                return 'bg-red-500/20 border-red-500/30 text-red-400';
            case 'medium':
                return 'bg-amber-500/20 border-amber-500/30 text-amber-400';
            default:
                return 'bg-zinc-700/50 border-zinc-600 text-zinc-400';
        }
    };
    
    // Market strength styling
    const getMarketStyle = (strength) => {
        switch (strength) {
            case 'strong':
                return { color: 'text-green-400', bg: 'bg-green-500/20' };
            case 'weak':
                return { color: 'text-red-400', bg: 'bg-red-500/20' };
            default:
                return { color: 'text-amber-400', bg: 'bg-amber-500/20' };
        }
    };
    
    if (loading) {
        return (
            <Card className="bg-zinc-900/50 border-zinc-800 border-2 border-purple-500/30">
                <CardContent className="p-6 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-500 mr-3" />
                    <span className="text-zinc-400">Analyzing dispatch decision...</span>
                </CardContent>
            </Card>
        );
    }
    
    if (!data) {
        return null;
    }
    
    const style = getDecisionStyle(data.decision);
    const marketStyle = getMarketStyle(data.market_analysis?.destination_strength);
    const reloadStyle = getMarketStyle(data.market_analysis?.reload_probability === 'high' ? 'strong' : 
                                       data.market_analysis?.reload_probability === 'low' ? 'weak' : 'medium');
    
    return (
        <Card className={`border-2 ${style.border} ${style.bg}`} data-testid="dispatch-decision-panel">
            {/* Main Decision Header */}
            <div 
                className="p-4 cursor-pointer"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {style.icon}
                        <div>
                            <h2 className={`text-2xl font-bold ${style.text}`}>{style.label}</h2>
                            <p className="text-zinc-400 text-sm mt-1">
                                {data.load_context?.origin} → {data.load_context?.destination}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-right">
                            <p className="text-zinc-500 text-xs">Current Rate</p>
                            <p className="text-white text-xl font-bold">
                                ${data.load_context?.current_rate?.toLocaleString()}
                            </p>
                        </div>
                        <ChevronDown className={`w-6 h-6 text-zinc-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                    </div>
                </div>
                
                {/* AI Insight - Always visible */}
                {data.ai_insights?.advice && (
                    <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                        <div className="flex items-start gap-2">
                            <Zap className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                            <p className="text-purple-300 text-sm">{data.ai_insights.advice}</p>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Expanded Details */}
            {expanded && (
                <CardContent className="pt-0 space-y-4">
                    {/* Decision Reasons */}
                    <div className="border-t border-zinc-700 pt-4">
                        <h4 className="text-zinc-300 font-semibold text-sm mb-3 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-purple-500" />
                            Decision Reasons
                        </h4>
                        <div className="space-y-2">
                            {data.decision_reasons?.map((reason, idx) => (
                                <p key={idx} className="text-zinc-400 text-sm pl-2 border-l-2 border-zinc-700">
                                    {reason}
                                </p>
                            ))}
                        </div>
                    </div>
                    
                    {/* Market Analysis & Positioning */}
                    <div className="grid grid-cols-2 gap-4">
                        {/* Market Analysis */}
                        <div className="bg-zinc-800/50 rounded-lg p-3">
                            <h4 className="text-zinc-300 font-semibold text-xs mb-3 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-blue-500" />
                                MARKET ANALYSIS
                            </h4>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-zinc-500 text-xs">Destination</span>
                                    <Badge className={`${marketStyle.bg} ${marketStyle.color} text-xs`}>
                                        {data.market_analysis?.destination_strength?.toUpperCase()}
                                    </Badge>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-zinc-500 text-xs">Reload Probability</span>
                                    <Badge className={`${reloadStyle.bg} ${reloadStyle.color} text-xs`}>
                                        {data.market_analysis?.reload_probability?.toUpperCase()}
                                    </Badge>
                                </div>
                                <p className="text-zinc-500 text-xs mt-2 italic">
                                    {data.market_analysis?.destination_note}
                                </p>
                            </div>
                        </div>
                        
                        {/* Positioning Impact */}
                        <div className="bg-zinc-800/50 rounded-lg p-3">
                            <h4 className="text-zinc-300 font-semibold text-xs mb-3 flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-orange-500" />
                                POSITIONING
                            </h4>
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-zinc-500 text-xs">Ends Up</span>
                                    <span className="text-zinc-300 text-sm">{data.positioning_impact?.final_location}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-zinc-500 text-xs">Reload Difficulty</span>
                                    <Badge className={`text-xs ${
                                        data.positioning_impact?.reload_difficulty === 'easy' ? 'bg-green-500/20 text-green-400' :
                                        data.positioning_impact?.reload_difficulty === 'hard' ? 'bg-red-500/20 text-red-400' :
                                        'bg-amber-500/20 text-amber-400'
                                    }`}>
                                        {data.positioning_impact?.reload_difficulty?.toUpperCase()}
                                    </Badge>
                                </div>
                                {data.positioning_impact?.estimated_next_deadhead > 0 && (
                                    <p className="text-zinc-500 text-xs mt-1">
                                        Est. next deadhead: ~{data.positioning_impact.estimated_next_deadhead} mi
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Risk Flags */}
                    {data.risk_flags?.length > 0 && (
                        <div>
                            <h4 className="text-zinc-300 font-semibold text-xs mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-red-500" />
                                RISK FLAGS
                            </h4>
                            <div className="flex flex-wrap gap-2">
                                {data.risk_flags.map((flag, idx) => (
                                    <div 
                                        key={idx}
                                        className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 ${getRiskStyle(flag.severity)}`}
                                    >
                                        <span>{flag.icon}</span>
                                        <span>{flag.message}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    {/* Enhanced Negotiation Guidance */}
                    {data.negotiation_guidance && (
                        <div className="border-t border-zinc-700 pt-4">
                            <h4 className="text-zinc-300 font-semibold text-xs mb-3 flex items-center gap-2">
                                <DollarSign className="w-4 h-4 text-green-500" />
                                NEGOTIATION GUIDANCE
                            </h4>
                            
                            {/* Rate Targets */}
                            <div className="grid grid-cols-3 gap-3 mb-4">
                                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                                    <p className="text-green-400 text-xs mb-1">ASK</p>
                                    <p className="text-green-400 text-xl font-bold">
                                        ${data.negotiation_guidance.ask?.toLocaleString()}
                                    </p>
                                </div>
                                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-center">
                                    <p className="text-blue-400 text-xs mb-1">TARGET</p>
                                    <p className="text-blue-400 text-xl font-bold">
                                        ${data.negotiation_guidance.target?.toLocaleString()}
                                    </p>
                                </div>
                                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
                                    <p className="text-amber-400 text-xs mb-1">FLOOR</p>
                                    <p className="text-amber-400 text-xl font-bold">
                                        ${data.negotiation_guidance.floor?.toLocaleString()}
                                    </p>
                                </div>
                            </div>
                            
                            {/* Context-Based Reasoning */}
                            {data.negotiation_guidance.reasoning?.length > 0 && (
                                <div className="bg-zinc-800/50 rounded-lg p-3">
                                    <p className="text-zinc-500 text-xs mb-2">WHY WE'RE ASKING HIGHER:</p>
                                    <ul className="space-y-1">
                                        {data.negotiation_guidance.reasoning.map((reason, idx) => (
                                            <li key={idx} className="text-zinc-400 text-sm flex items-start gap-2">
                                                <span className="text-purple-400">•</span>
                                                {reason}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            {/* Approach */}
                            {data.negotiation_guidance.approach && (
                                <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                                    <p className="text-purple-400 text-sm font-medium">
                                        Approach: {data.negotiation_guidance.approach}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Historical Insights Section */}
                    {data.historical_insights && (
                        <div className="border-t border-zinc-700 pt-4">
                            <h4 className="text-zinc-300 font-semibold text-xs mb-3 flex items-center gap-2">
                                <Clock className="w-4 h-4 text-cyan-500" />
                                HISTORICAL INSIGHT
                                {data.historical_insights.has_history && (
                                    <Badge className="bg-cyan-500/20 text-cyan-400 text-xs">ADAPTIVE</Badge>
                                )}
                            </h4>
                            
                            {data.historical_insights.has_history ? (
                                <div className="space-y-3">
                                    {/* Lane History */}
                                    {data.historical_insights.lane_history_summary && (
                                        <div className="bg-zinc-800/50 rounded-lg p-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-zinc-400 text-xs">LANE PERFORMANCE</span>
                                                <Badge className={`text-xs ${
                                                    data.historical_insights.lane_history_summary.success_rate >= 80 
                                                        ? 'bg-green-500/20 text-green-400' 
                                                        : data.historical_insights.lane_history_summary.success_rate >= 60
                                                        ? 'bg-amber-500/20 text-amber-400'
                                                        : 'bg-red-500/20 text-red-400'
                                                }`}>
                                                    {data.historical_insights.lane_history_summary.success_rate?.toFixed(0)}% Success
                                                </Badge>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-sm">
                                                <div>
                                                    <span className="text-zinc-500">Past Loads:</span>
                                                    <span className="text-zinc-300 ml-2">{data.historical_insights.lane_history_summary.total_loads}</span>
                                                </div>
                                                <div>
                                                    <span className="text-zinc-500">Avg Rate:</span>
                                                    <span className="text-zinc-300 ml-2">
                                                        ${data.historical_insights.lane_history_summary.average_rate?.toLocaleString()}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Broker Reliability */}
                                    {data.historical_insights.broker_reliability !== null && data.historical_insights.broker_reliability !== undefined && (
                                        <div className="bg-zinc-800/50 rounded-lg p-3">
                                            <div className="flex items-center justify-between">
                                                <span className="text-zinc-400 text-xs">BROKER RELIABILITY</span>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-20 h-2 bg-zinc-700 rounded-full overflow-hidden">
                                                        <div 
                                                            className={`h-full rounded-full ${
                                                                data.historical_insights.broker_reliability >= 80 ? 'bg-green-500' :
                                                                data.historical_insights.broker_reliability >= 60 ? 'bg-amber-500' : 'bg-red-500'
                                                            }`}
                                                            style={{ width: `${data.historical_insights.broker_reliability}%` }}
                                                        ></div>
                                                    </div>
                                                    <span className={`text-sm font-medium ${
                                                        data.historical_insights.broker_reliability >= 80 ? 'text-green-400' :
                                                        data.historical_insights.broker_reliability >= 60 ? 'text-amber-400' : 'text-red-400'
                                                    }`}>
                                                        {data.historical_insights.broker_reliability?.toFixed(0)}/100
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Warnings from History */}
                                    {data.historical_insights.warnings?.length > 0 && (
                                        <div className="space-y-2">
                                            {data.historical_insights.warnings.map((warning, idx) => (
                                                <div key={idx} className={`p-2 rounded-lg border text-sm flex items-center gap-2 ${
                                                    warning.severity === 'high' 
                                                        ? 'bg-red-500/10 border-red-500/30 text-red-400'
                                                        : 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                                                }`}>
                                                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                                    {warning.message}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* Positives from History */}
                                    {data.historical_insights.positives?.length > 0 && (
                                        <div className="space-y-2">
                                            {data.historical_insights.positives.map((positive, idx) => (
                                                <div key={idx} className="p-2 rounded-lg border bg-green-500/10 border-green-500/30 text-green-400 text-sm flex items-center gap-2">
                                                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                                                    {positive.message}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
                                    <p className="text-zinc-500 text-sm">
                                        No historical data yet - complete loads to build adaptive intelligence
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </CardContent>
            )}
        </Card>
    );
}


// ==================== LOAD OUTCOME FEEDBACK ====================
// Simple UI to rate load outcomes after completion

function LoadOutcomeFeedback({ loadId, loadStatus, onFeedbackSubmitted }) {
    const [outcome, setOutcome] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [existingOutcome, setExistingOutcome] = useState(null);
    
    // Check if outcome already recorded
    useEffect(() => {
        const checkExisting = async () => {
            try {
                const res = await fetch(`${API_URL}/api/learning/load/${loadId}/outcome`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.outcome_recorded) {
                        setExistingOutcome(data.outcome);
                        setSubmitted(true);
                    }
                }
            } catch (e) {
                console.error('Failed to check outcome:', e);
            }
        };
        
        if (loadId) checkExisting();
    }, [loadId]);
    
    // Only show for completed/delivered loads
    const showFeedback = loadStatus === 'delivered' || loadStatus === 'completed';
    
    if (!showFeedback) return null;
    
    const handleSubmit = async (selectedOutcome) => {
        setSubmitting(true);
        try {
            const res = await fetch(`${API_URL}/api/learning/load/${loadId}/outcome`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ outcome: selectedOutcome })
            });
            
            if (res.ok) {
                setOutcome(selectedOutcome);
                setSubmitted(true);
                toast.success(`Load rated as ${selectedOutcome}`, {
                    description: 'Thanks! This helps improve future decisions.'
                });
                onFeedbackSubmitted?.(selectedOutcome);
            } else {
                toast.error('Failed to submit feedback');
            }
        } catch (e) {
            toast.error('Failed to submit feedback');
        } finally {
            setSubmitting(false);
        }
    };
    
    const outcomeOptions = [
        { value: 'good', label: '👍 Good', description: 'Profitable & smooth', color: 'bg-green-500/20 border-green-500/50 hover:bg-green-500/30' },
        { value: 'neutral', label: '😐 Neutral', description: 'Average, no issues', color: 'bg-amber-500/20 border-amber-500/50 hover:bg-amber-500/30' },
        { value: 'bad', label: '👎 Bad', description: 'Problems occurred', color: 'bg-red-500/20 border-red-500/50 hover:bg-red-500/30' }
    ];
    
    if (submitted) {
        const finalOutcome = existingOutcome || outcome;
        const selectedOption = outcomeOptions.find(o => o.value === finalOutcome);
        return (
            <Card className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-cyan-500">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-cyan-500" />
                            <div>
                                <p className="text-zinc-300 text-sm font-medium">Load Outcome Recorded</p>
                                <p className="text-zinc-500 text-xs">Rating: {selectedOption?.label || finalOutcome}</p>
                            </div>
                        </div>
                        <Badge className="bg-cyan-500/20 text-cyan-400">Learning Active</Badge>
                    </div>
                </CardContent>
            </Card>
        );
    }
    
    return (
        <Card className="bg-zinc-900/50 border-zinc-800 border-2 border-cyan-500/30" data-testid="load-outcome-feedback">
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-cyan-500" />
                    How Was This Load?
                    <Badge variant="outline" className="text-xs border-cyan-500/50 text-cyan-400">Feedback</Badge>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <p className="text-zinc-400 text-sm mb-4">
                    Your feedback helps the system make better decisions for future loads.
                </p>
                <div className="grid grid-cols-3 gap-3">
                    {outcomeOptions.map((option) => (
                        <button
                            key={option.value}
                            onClick={() => handleSubmit(option.value)}
                            disabled={submitting}
                            className={`p-4 rounded-lg border text-center transition-all ${option.color} ${
                                submitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                            }`}
                            data-testid={`outcome-${option.value}`}
                        >
                            <p className="text-2xl mb-1">{option.label.split(' ')[0]}</p>
                            <p className="text-zinc-300 text-sm font-medium">{option.label.split(' ')[1]}</p>
                            <p className="text-zinc-500 text-xs mt-1">{option.description}</p>
                        </button>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}


// Green Line Calculator Component
function GreenLineCalculator({ loadId, currentRate, distance }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cpmEditing, setCpmEditing] = useState(false);
    const [newCpm, setNewCpm] = useState('');

    const fetchGreenLine = useCallback(async () => {
        if (!loadId) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/negotiation/green-line/${loadId}`, {
                credentials: 'include'
            });
            if (res.ok) {
                const result = await res.json();
                setData(result);
                setNewCpm(result.cost_per_mile?.toString() || '1.75');
            }
        } catch (e) {
            console.error('Failed to fetch green line:', e);
        } finally {
            setLoading(false);
        }
    }, [loadId]);

    useEffect(() => {
        fetchGreenLine();
    }, [fetchGreenLine]);

    const updateCpm = async () => {
        if (!data?.truck_id) return;
        try {
            const res = await fetch(`${API_URL}/api/trucks/${data.truck_id}/cpm?cpm=${parseFloat(newCpm)}`, {
                method: 'PATCH',
                credentials: 'include'
            });
            if (res.ok) {
                toast.success('CPM updated');
                setCpmEditing(false);
                fetchGreenLine();
            } else {
                toast.error('Failed to update CPM');
            }
        } catch (e) {
            toast.error('Error updating CPM');
        }
    };

    if (loading) {
        return <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-green-500" /></div>;
    }

    if (!data) return null;

    const isProfitable = data.status === 'PROFITABLE';

    return (
        <div className={`rounded-lg p-4 ${isProfitable ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-zinc-200 font-semibold flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${isProfitable ? 'bg-green-500' : 'bg-red-500'}`} />
                    GREEN LINE
                </h4>
                {data.truck_name && (
                    <span className="text-xs text-zinc-500">{data.truck_name}</span>
                )}
            </div>
            
            {/* CPM Editor */}
            <div className="flex items-center gap-2 mb-3 text-sm">
                <span className="text-zinc-400">CPM:</span>
                {cpmEditing ? (
                    <>
                        <input
                            type="number"
                            step="0.05"
                            min="0.50"
                            max="5.00"
                            value={newCpm}
                            onChange={(e) => setNewCpm(e.target.value)}
                            className="w-20 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-zinc-200"
                        />
                        <Button size="sm" variant="ghost" onClick={updateCpm} className="h-6 px-2">
                            <Check className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setCpmEditing(false)} className="h-6 px-2">
                            <X className="w-3 h-3" />
                        </Button>
                    </>
                ) : (
                    <>
                        <span className="text-zinc-200 font-medium">${data.cost_per_mile?.toFixed(2)}/mi</span>
                        <Button size="sm" variant="ghost" onClick={() => setCpmEditing(true)} className="h-6 px-2">
                            <Edit className="w-3 h-3" />
                        </Button>
                    </>
                )}
            </div>

            {/* Green Line Calculation */}
            <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                    <p className="text-xs text-zinc-500">Base Cost ({data.distance} mi × ${data.cost_per_mile})</p>
                    <p className="text-lg font-semibold text-zinc-300">${data.base_operating_cost?.toLocaleString()}</p>
                </div>
                <div>
                    <p className="text-xs text-zinc-500">+ 10% Dispatcher Fee</p>
                    <p className="text-lg font-semibold text-zinc-300">${data.dispatcher_fee_10_percent?.toLocaleString()}</p>
                </div>
            </div>

            {/* Green Line Result */}
            <div className={`p-3 rounded ${isProfitable ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                <div className="flex justify-between items-center">
                    <div>
                        <p className="text-xs text-zinc-400">GREEN LINE (Minimum)</p>
                        <p className={`text-2xl font-bold ${isProfitable ? 'text-green-400' : 'text-red-400'}`}>
                            ${data.green_line?.toLocaleString()}
                        </p>
                        <p className="text-xs text-zinc-500">${data.green_line_rpm?.toFixed(2)}/mi</p>
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-zinc-400">Current Offer</p>
                        <p className="text-2xl font-bold text-zinc-200">${data.current_rate?.toLocaleString()}</p>
                        <p className="text-xs text-zinc-500">${data.current_rpm?.toFixed(2)}/mi</p>
                    </div>
                </div>
                {isProfitable ? (
                    <p className="text-sm text-green-400 mt-2">
                        ✓ +${data.profit_margin?.toLocaleString()} profit margin ({data.profit_percentage?.toFixed(1)}% above green line)
                    </p>
                ) : (
                    <p className="text-sm text-red-400 mt-2">
                        ⚠ ${Math.abs(data.profit_margin)?.toLocaleString()} BELOW your green line - negotiate higher!
                    </p>
                )}
            </div>

            {/* Negotiation Strategy Section */}
            {data.negotiation_strategy && (
                <div className="mt-4 pt-4 border-t border-zinc-700">
                    <h5 className="text-zinc-300 font-semibold mb-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-blue-400" />
                        NEGOTIATION STRATEGY
                    </h5>
                    
                    {/* Price Ladder */}
                    <div className="space-y-2 mb-4">
                        {/* Opening Ask */}
                        <div className="flex items-center justify-between p-2 bg-blue-500/10 border border-blue-500/30 rounded">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-blue-400" />
                                <span className="text-zinc-300 text-sm font-medium">Opening Ask</span>
                            </div>
                            <div className="text-right">
                                <span className="text-blue-400 font-bold text-lg">
                                    ${data.negotiation_strategy.opening_ask?.toLocaleString()}
                                </span>
                                <span className="text-zinc-500 text-xs ml-2">
                                    (${data.negotiation_strategy.opening_ask_rpm}/mi)
                                </span>
                            </div>
                        </div>
                        
                        {/* Target Range */}
                        <div className="flex items-center justify-between p-2 bg-amber-500/10 border border-amber-500/30 rounded">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-amber-400" />
                                <span className="text-zinc-300 text-sm font-medium">Target Range</span>
                            </div>
                            <div className="text-right">
                                <span className="text-amber-400 font-bold">
                                    ${data.negotiation_strategy.target_range?.low?.toLocaleString()} - ${data.negotiation_strategy.target_range?.high?.toLocaleString()}
                                </span>
                                <span className="text-zinc-500 text-xs ml-2">
                                    (${data.negotiation_strategy.target_range?.low_rpm}-${data.negotiation_strategy.target_range?.high_rpm}/mi)
                                </span>
                            </div>
                        </div>
                        
                        {/* Floor Price */}
                        <div className="flex items-center justify-between p-2 bg-red-500/10 border border-red-500/30 rounded">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-red-400" />
                                <span className="text-zinc-300 text-sm font-medium">Floor (Walk Away)</span>
                            </div>
                            <div className="text-right">
                                <span className="text-red-400 font-bold text-lg">
                                    ${data.negotiation_strategy.floor_price?.toLocaleString()}
                                </span>
                                <span className="text-zinc-500 text-xs ml-2">
                                    (${data.negotiation_strategy.floor_rpm}/mi)
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Market Reference */}
                    {data.negotiation_strategy.market_rate_estimate > 0 && (
                        <p className="text-xs text-zinc-500 mb-3">
                            Market rate estimate: ${data.negotiation_strategy.market_rate_estimate?.toLocaleString()} 
                            (${data.negotiation_strategy.market_rpm}/mi)
                        </p>
                    )}
                </div>
            )}

            {/* Negotiation Reasons */}
            {data.negotiation_reasons && data.negotiation_reasons.length > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-700">
                    <h5 className="text-zinc-300 font-semibold mb-3 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-purple-400" />
                        NEGOTIATION TALKING POINTS
                    </h5>
                    <div className="space-y-2">
                        {data.negotiation_reasons.map((reason, idx) => (
                            <div 
                                key={idx} 
                                className={`p-2 rounded text-sm ${
                                    reason.severity === 'critical' ? 'bg-red-500/10 border border-red-500/30' :
                                    reason.severity === 'high' ? 'bg-amber-500/10 border border-amber-500/30' :
                                    reason.severity === 'medium' ? 'bg-blue-500/10 border border-blue-500/30' :
                                    'bg-zinc-700/30 border border-zinc-600'
                                }`}
                            >
                                <div className="flex items-start gap-2">
                                    <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded ${
                                        reason.severity === 'critical' ? 'bg-red-500/30 text-red-400' :
                                        reason.severity === 'high' ? 'bg-amber-500/30 text-amber-400' :
                                        reason.severity === 'medium' ? 'bg-blue-500/30 text-blue-400' :
                                        'bg-zinc-600 text-zinc-400'
                                    }`}>
                                        {reason.type}
                                    </span>
                                </div>
                                <p className="text-zinc-300 mt-1">{reason.reason}</p>
                                <p className="text-zinc-500 text-xs mt-1 italic">
                                    💬 "{reason.counter}"
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// Backhaul Suggestions Component
function BackhaulSuggestions({ loadId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!loadId) return;
        const fetchBackhauls = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_URL}/api/negotiation/backhaul-suggestions/${loadId}`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    setData(await res.json());
                }
            } catch (e) {
                console.error('Failed to fetch backhaul suggestions:', e);
            } finally {
                setLoading(false);
            }
        };
        fetchBackhauls();
    }, [loadId]);

    if (loading) {
        return <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-blue-500" /></div>;
    }

    if (!data) return null;

    return (
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
            <button 
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between"
            >
                <h4 className="text-zinc-200 font-semibold flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-500" />
                    LANE OPTIMIZER
                </h4>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-zinc-500">Delivering to {data.delivery_location}</span>
                    <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </div>
            </button>

            {expanded && (
                <div className="mt-4 space-y-3">
                    {data.suggestions?.length > 0 ? (
                        data.suggestions.map((s, idx) => (
                            <div key={idx} className="bg-zinc-900/50 border border-zinc-700 rounded p-3">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <p className="text-zinc-200 font-medium">{s.origin} → {s.destination}</p>
                                        <p className="text-xs text-zinc-500">{s.distance} mi • {s.equipment_type || 'Dry Van'}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-green-400 font-bold">${s.gross_pay?.toLocaleString()}</p>
                                        <p className="text-xs text-zinc-500">${s.rate_per_mile}/mi</p>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {s.reasons?.map((r, i) => (
                                        <span key={i} className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                                            {r}
                                        </span>
                                    ))}
                                </div>
                                <p className="text-xs text-zinc-500 mt-2">
                                    ~{s.estimated_deadhead_miles} mi deadhead • Broker: {s.broker_name || 'Unknown'}
                                </p>
                            </div>
                        ))
                    ) : (
                        <p className="text-zinc-500 text-center py-4">No backhaul suggestions within 300 miles</p>
                    )}
                    
                    {data.home_base && data.home_base !== 'Not set' && (
                        <p className="text-xs text-zinc-500 text-center">
                            Home base: {data.home_base}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}

// Lane Dossier Component
function LaneDossier({ load }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showNotes, setShowNotes] = useState(false);
    const [notes, setNotes] = useState('');
    const [savingNotes, setSavingNotes] = useState(false);

    const originCity = load?.origin_city;
    const originState = load?.origin_state;
    const destCity = load?.destination_city;
    const destState = load?.destination_state;

    useEffect(() => {
        if (!originCity || !destCity) return;
        
        const fetchDossier = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    origin_city: originCity,
                    origin_state: originState || '',
                    destination_city: destCity,
                    destination_state: destState || ''
                });
                const res = await fetch(`${API_URL}/api/negotiation/lane-dossier?${params}`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    const result = await res.json();
                    setData(result);
                    setNotes(result.notes || '');
                }
            } catch (e) {
                console.error('Failed to fetch lane dossier:', e);
            } finally {
                setLoading(false);
            }
        };
        fetchDossier();
    }, [originCity, originState, destCity, destState]);

    const saveNotes = async () => {
        if (!originCity || !destCity) return;
        setSavingNotes(true);
        try {
            const params = new URLSearchParams({
                origin_city: originCity,
                origin_state: originState || '',
                destination_city: destCity,
                destination_state: destState || '',
                notes: notes
            });
            const res = await fetch(`${API_URL}/api/negotiation/lane-dossier?${params}`, {
                method: 'POST',
                credentials: 'include'
            });
            if (res.ok) {
                toast.success('Lane notes saved');
                setShowNotes(false);
            }
        } catch (e) {
            toast.error('Failed to save notes');
        } finally {
            setSavingNotes(false);
        }
    };

    if (!originCity || !destCity) return null;
    if (loading) {
        return <div className="flex items-center justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-purple-500" /></div>;
    }

    const stats = data?.statistics;

    return (
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
                <h4 className="text-zinc-200 font-semibold flex items-center gap-2">
                    <FileText className="w-4 h-4 text-purple-500" />
                    LANE DOSSIER
                </h4>
                <Button size="sm" variant="ghost" onClick={() => setShowNotes(!showNotes)} className="h-6 text-xs">
                    {showNotes ? 'Hide Notes' : 'Add Notes'}
                </Button>
            </div>

            <p className="text-sm text-zinc-400 mb-3">
                {data?.lane?.origin} → {data?.lane?.destination}
            </p>

            {stats?.total_loads > 0 ? (
                <>
                    <div className="grid grid-cols-4 gap-2 mb-3">
                        <div className="text-center">
                            <p className="text-xs text-zinc-500">Loads</p>
                            <p className="text-lg font-bold text-zinc-200">{stats.total_loads}</p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs text-zinc-500">High</p>
                            <p className="text-lg font-bold text-green-400">${stats.high_rate?.toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs text-zinc-500">Avg</p>
                            <p className="text-lg font-bold text-blue-400">${stats.avg_rate?.toLocaleString()}</p>
                        </div>
                        <div className="text-center">
                            <p className="text-xs text-zinc-500">Low</p>
                            <p className="text-lg font-bold text-red-400">${stats.low_rate?.toLocaleString()}</p>
                        </div>
                    </div>

                    {data?.broker_performance?.length > 0 && (
                        <div className="mb-3">
                            <p className="text-xs text-zinc-500 mb-1">Top Brokers on this Lane:</p>
                            <div className="flex flex-wrap gap-2">
                                {data.broker_performance.slice(0, 3).map((b, i) => (
                                    <span key={i} className="text-xs bg-purple-500/20 text-purple-400 px-2 py-1 rounded">
                                        {b.broker_name}: ${b.avg_rate?.toLocaleString()} avg ({b.loads_completed} loads)
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <p className="text-zinc-500 text-sm text-center py-2">No historical data for this lane</p>
            )}

            {showNotes && (
                <div className="mt-3 space-y-2">
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Add notes about this lane, broker behavior, seasonal patterns..."
                        className="w-full h-24 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-200 text-sm resize-none"
                    />
                    <div className="flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setShowNotes(false)}>Cancel</Button>
                        <Button size="sm" onClick={saveNotes} disabled={savingNotes}>
                            {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Notes'}
                        </Button>
                    </div>
                </div>
            )}

            {data?.notes && !showNotes && (
                <div className="mt-2 p-2 bg-zinc-900/50 rounded text-sm text-zinc-400 italic">
                    "{data.notes}"
                </div>
            )}
        </div>
    );
}

// Broker Vetting Alert Component  
function BrokerVettingAlert({ brokerId, brokerName }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!brokerId) return;
        
        const fetchVetting = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_URL}/api/negotiation/broker-vetting/${brokerId}`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    setData(await res.json());
                }
            } catch (e) {
                console.error('Failed to fetch broker vetting:', e);
            } finally {
                setLoading(false);
            }
        };
        fetchVetting();
    }, [brokerId]);

    if (loading || !data) return null;

    // Only show if there's a warning
    if (!data.warning && data.found) return null;

    const alertStyle = data.warning_type === 'HARD_GATE' 
        ? 'bg-red-500/20 border-red-500/50 text-red-400'
        : data.warning_type === 'MEDIUM_CREDIT'
        ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
        : 'bg-zinc-700/50 border-zinc-600 text-zinc-400';

    return (
        <div className={`rounded-lg border p-4 ${alertStyle}`} data-testid="broker-vetting-alert">
            <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                    <p className="font-semibold">{data.message}</p>
                    <p className="text-sm mt-1 opacity-80">{data.recommendation}</p>
                    {data.credit_score !== undefined && (
                        <div className="flex items-center gap-4 mt-2 text-sm">
                            <span>Credit Score: <strong>{data.credit_score}</strong></span>
                            {data.days_to_pay && <span>Avg Pay: <strong>{data.days_to_pay} days</strong></span>}
                            {data.historical_loads_completed > 0 && (
                                <span>History: <strong>{data.historical_loads_completed} loads</strong></span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


// ==================== BROKER RISK ANALYSIS COMPONENT ====================

function BrokerRiskAnalysis({ loadId, onRiskAssessed }) {
    const [riskData, setRiskData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    
    useEffect(() => {
        if (!loadId) return;
        
        const fetchRiskAnalysis = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_URL}/api/operations/queue/${loadId}/broker-risk?include_ai_insights=true`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    const data = await res.json();
                    setRiskData(data);
                    // Notify parent of risk assessment
                    onRiskAssessed?.(data);
                }
            } catch (e) {
                console.error('Failed to fetch broker risk:', e);
            } finally {
                setLoading(false);
            }
        };
        
        fetchRiskAnalysis();
    }, [loadId, onRiskAssessed]);
    
    if (loading) {
        return (
            <div className="flex items-center gap-2 p-3 bg-zinc-800/50 rounded-lg">
                <Loader2 className="w-4 h-4 animate-spin text-purple-500" />
                <span className="text-zinc-400 text-sm">Analyzing broker risk...</span>
            </div>
        );
    }
    
    if (!riskData || riskData.no_broker_data) {
        return (
            <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    <span className="text-amber-400 text-sm">No broker data available</span>
                </div>
            </div>
        );
    }
    
    const { risk_score, risk_level, components, ai_insights, warning_message, can_book_without_override } = riskData;
    
    // Risk level styling
    const getRiskStyle = () => {
        if (risk_level === 'low') return {
            bgColor: 'bg-green-500/10',
            borderColor: 'border-green-500/30',
            textColor: 'text-green-400',
            badgeColor: 'bg-green-500',
            icon: <CheckCircle className="w-5 h-5 text-green-500" />,
            label: '✅ LOW RISK'
        };
        if (risk_level === 'caution') return {
            bgColor: 'bg-amber-500/10',
            borderColor: 'border-amber-500/30',
            textColor: 'text-amber-400',
            badgeColor: 'bg-amber-500',
            icon: <AlertTriangle className="w-5 h-5 text-amber-500" />,
            label: '⚠️ CAUTION'
        };
        return {
            bgColor: 'bg-red-500/10',
            borderColor: 'border-red-500/30',
            textColor: 'text-red-400',
            badgeColor: 'bg-red-500',
            icon: <XCircle className="w-5 h-5 text-red-500" />,
            label: '❌ HIGH RISK'
        };
    };
    
    const style = getRiskStyle();
    
    return (
        <div className={`rounded-lg border ${style.borderColor} ${style.bgColor} overflow-hidden`} data-testid="broker-risk-analysis">
            {/* Header */}
            <div 
                className="p-4 cursor-pointer hover:bg-zinc-800/30 transition-colors"
                onClick={() => setShowDetails(!showDetails)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {style.icon}
                        <div>
                            <div className="flex items-center gap-2">
                                <span className={`font-semibold ${style.textColor}`}>{style.label}</span>
                                <Badge className={`${style.badgeColor} text-white font-bold`}>
                                    {risk_score}/100
                                </Badge>
                            </div>
                            <p className="text-zinc-400 text-sm mt-1">{riskData.broker_name || 'Broker'}</p>
                        </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-zinc-400 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
                </div>
                
                {/* Warning message for high risk */}
                {risk_level === 'high' && (
                    <div className="mt-3 p-2 bg-red-500/20 border border-red-500/30 rounded">
                        <p className="text-red-400 text-sm font-medium">
                            {warning_message || 'High-risk broker - proceed with caution'}
                        </p>
                    </div>
                )}
            </div>
            
            {/* Expandable Details */}
            {showDetails && (
                <div className="border-t border-zinc-700 p-4 space-y-4">
                    {/* Score Components */}
                    <div className="space-y-3">
                        <p className="text-zinc-400 text-xs uppercase tracking-wider font-semibold">Risk Breakdown</p>
                        
                        {/* Credit Score Component */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                <span className="text-zinc-300 text-sm">Credit Score</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-zinc-700 rounded-full overflow-hidden">
                                    <div 
                                        className="h-full bg-blue-500 rounded-full"
                                        style={{ width: `${components?.credit?.score || 0}%` }}
                                    ></div>
                                </div>
                                <span className="text-zinc-400 text-xs w-8">{Math.round(components?.credit?.score || 0)}</span>
                            </div>
                        </div>
                        
                        {/* Payment Speed Component */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                <span className="text-zinc-300 text-sm">Payment Speed</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-zinc-700 rounded-full overflow-hidden">
                                    <div 
                                        className="h-full bg-green-500 rounded-full"
                                        style={{ width: `${components?.payment?.score || 0}%` }}
                                    ></div>
                                </div>
                                <span className="text-zinc-400 text-xs w-8">{Math.round(components?.payment?.score || 0)}</span>
                            </div>
                        </div>
                        
                        {/* Historical Component */}
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                                <span className="text-zinc-300 text-sm">History</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-zinc-700 rounded-full overflow-hidden">
                                    <div 
                                        className="h-full bg-purple-500 rounded-full"
                                        style={{ width: `${components?.historical?.score || 0}%` }}
                                    ></div>
                                </div>
                                <span className="text-zinc-400 text-xs w-8">{Math.round(components?.historical?.score || 0)}</span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Component Explanations */}
                    <div className="space-y-2 pt-3 border-t border-zinc-700">
                        <p className="text-zinc-400 text-xs uppercase tracking-wider font-semibold">Details</p>
                        {components?.credit?.explanation && (
                            <p className="text-zinc-400 text-sm">• {components.credit.explanation}</p>
                        )}
                        {components?.payment?.explanation && (
                            <p className="text-zinc-400 text-sm">• {components.payment.explanation}</p>
                        )}
                        {components?.historical?.explanations?.map((exp, i) => (
                            <p key={i} className="text-zinc-400 text-sm">• {exp}</p>
                        ))}
                    </div>
                    
                    {/* AI Insights */}
                    {ai_insights && (
                        <div className="space-y-3 pt-3 border-t border-zinc-700">
                            <div className="flex items-center gap-2">
                                <Zap className="w-4 h-4 text-purple-500" />
                                <p className="text-purple-400 text-xs uppercase tracking-wider font-semibold">AI Insights</p>
                                {ai_insights.ai_generated && (
                                    <Badge variant="outline" className="text-xs border-purple-500/50 text-purple-400">Gemini</Badge>
                                )}
                            </div>
                            
                            {ai_insights.summary && (
                                <p className="text-zinc-300 text-sm">{ai_insights.summary}</p>
                            )}
                            
                            {ai_insights.key_concerns?.length > 0 && (
                                <div className="space-y-1">
                                    <p className="text-red-400 text-xs font-medium">Key Concerns:</p>
                                    {ai_insights.key_concerns.map((concern, i) => (
                                        <p key={i} className="text-zinc-400 text-sm pl-2">• {concern}</p>
                                    ))}
                                </div>
                            )}
                            
                            {ai_insights.recommendations?.length > 0 && (
                                <div className="space-y-1">
                                    <p className="text-green-400 text-xs font-medium">Recommendations:</p>
                                    {ai_insights.recommendations.map((rec, i) => (
                                        <p key={i} className="text-zinc-400 text-sm pl-2">• {rec}</p>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


// ==================== NEGOTIATION SCRIPT GENERATOR ====================

function NegotiationScriptGenerator({ loadId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeSection, setActiveSection] = useState('opening');
    const [copiedId, setCopiedId] = useState(null);

    useEffect(() => {
        if (!loadId) return;
        const fetchScripts = async () => {
            setLoading(true);
            try {
                const res = await fetch(`${API_URL}/api/negotiation/scripts/${loadId}`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    setData(await res.json());
                }
            } catch (e) {
                console.error('Failed to fetch negotiation scripts:', e);
            } finally {
                setLoading(false);
            }
        };
        fetchScripts();
    }, [loadId]);

    const copyToClipboard = async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedId(id);
            toast.success('Script copied to clipboard!');
            setTimeout(() => setCopiedId(null), 2000);
        } catch (e) {
            toast.error('Failed to copy');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
            </div>
        );
    }

    if (!data) return null;

    const ScriptCard = ({ script, showTone = true }) => (
        <div className="bg-zinc-900/60 border border-zinc-700 rounded-lg p-3 hover:border-zinc-600 transition-colors">
            <div className="flex items-start justify-between gap-2 mb-2">
                <span className="text-xs font-medium text-purple-400 uppercase">{script.label}</span>
                <div className="flex items-center gap-1">
                    {showTone && script.tone && (
                        <span className="text-xs px-2 py-0.5 rounded bg-zinc-700 text-zinc-400">{script.tone}</span>
                    )}
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => copyToClipboard(script.script, script.id)}
                    >
                        {copiedId === script.id ? (
                            <Check className="w-3 h-3 text-green-400" />
                        ) : (
                            <Copy className="w-3 h-3 text-zinc-400" />
                        )}
                    </Button>
                </div>
            </div>
            <p className="text-zinc-200 text-sm leading-relaxed">"{script.script}"</p>
        </div>
    );

    return (
        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden" data-testid="negotiation-scripts">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 border-b border-zinc-700 p-4">
                <div className="flex items-center justify-between">
                    <h4 className="text-zinc-100 font-semibold flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-purple-400" />
                        NEGOTIATION SCRIPTS
                    </h4>
                    {data.ai_generated && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-purple-500/30 to-blue-500/30 rounded text-xs text-purple-300 border border-purple-500/30">
                            <Zap className="w-3 h-3" />
                            AI-Powered
                        </span>
                    )}
                </div>
                <p className="text-zinc-500 text-xs mt-1">
                    {data.load_summary?.origin} → {data.load_summary?.destination} • {data.load_summary?.distance} mi
                </p>
            </div>

            {/* Price Points Reference */}
            <div className="grid grid-cols-4 gap-2 p-3 bg-zinc-900/30 border-b border-zinc-700 text-center">
                <div>
                    <p className="text-xs text-zinc-500">Open At</p>
                    <p className="text-blue-400 font-bold">${data.price_points?.opening_ask?.toLocaleString()}</p>
                </div>
                <div>
                    <p className="text-xs text-zinc-500">Target</p>
                    <p className="text-amber-400 font-bold">${data.price_points?.target_low?.toLocaleString()}-${data.price_points?.target_high?.toLocaleString()}</p>
                </div>
                <div>
                    <p className="text-xs text-zinc-500">Floor</p>
                    <p className="text-red-400 font-bold">${data.price_points?.floor?.toLocaleString()}</p>
                </div>
                <div>
                    <p className="text-xs text-zinc-500">Current</p>
                    <p className="text-zinc-300 font-bold">${data.price_points?.current_offer?.toLocaleString() || '—'}</p>
                </div>
            </div>

            {/* Section Tabs */}
            <div className="flex border-b border-zinc-700">
                {[
                    { id: 'opening', label: 'Opening', icon: Phone },
                    { id: 'pushback', label: 'Pushbacks', icon: Shield },
                    { id: 'closing', label: 'Closing', icon: CheckCircle },
                    { id: 'quick', label: 'Quick', icon: Zap }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveSection(tab.id)}
                        className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors ${
                            activeSection === tab.id
                                ? 'bg-zinc-700/50 text-white border-b-2 border-purple-500'
                                : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-700/30'
                        }`}
                    >
                        <tab.icon className="w-3.5 h-3.5" />
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            <div className="p-4 max-h-96 overflow-y-auto">
                {/* Opening Scripts */}
                {activeSection === 'opening' && (
                    <div className="space-y-3">
                        <p className="text-xs text-zinc-500 mb-3">Start the negotiation with confidence:</p>
                        {data.opening_scripts?.map(script => (
                            <ScriptCard key={script.id} script={script} />
                        ))}
                    </div>
                )}

                {/* Pushback Responses */}
                {activeSection === 'pushback' && (
                    <div className="space-y-4">
                        {Object.entries(data.pushback_responses || {}).map(([key, section]) => (
                            <div key={key}>
                                <div className="flex items-center gap-2 mb-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                                    <span className="text-zinc-300 font-medium text-sm">"{section.objection}"</span>
                                </div>
                                <div className="space-y-2 pl-6">
                                    {section.responses?.map(script => (
                                        <ScriptCard key={script.id} script={script} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Closing Scripts */}
                {activeSection === 'closing' && (
                    <div className="space-y-3">
                        <p className="text-xs text-zinc-500 mb-3">Close the deal:</p>
                        {data.closing_scripts?.map(script => (
                            <div key={script.id}>
                                <ScriptCard script={script} />
                                {script.price_point && script.price_point !== 'walk' && (
                                    <p className="text-xs text-zinc-600 mt-1 pl-3">
                                        → Price point: {script.price_point.replace('_', ' ')}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Quick Responses */}
                {activeSection === 'quick' && (
                    <div className="space-y-2">
                        <p className="text-xs text-zinc-500 mb-3">Quick one-liners:</p>
                        {data.quick_responses?.map((item, idx) => (
                            <div 
                                key={idx} 
                                className="flex items-center justify-between p-2 bg-zinc-900/60 border border-zinc-700 rounded hover:border-zinc-600 transition-colors"
                            >
                                <div className="flex-1">
                                    <p className="text-zinc-200 text-sm">"{item.text}"</p>
                                    <p className="text-zinc-500 text-xs mt-0.5">{item.use}</p>
                                </div>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 w-7 p-0 ml-2"
                                    onClick={() => copyToClipboard(item.text, `quick-${idx}`)}
                                >
                                    {copiedId === `quick-${idx}` ? (
                                        <Check className="w-3 h-3 text-green-400" />
                                    ) : (
                                        <Copy className="w-3 h-3 text-zinc-400" />
                                    )}
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// ==================== END NEGOTIATION SCRIPT GENERATOR ====================

// Combined Negotiation Brain Panel
function NegotiationBrain({ load, onRefresh }) {
    const loadId = load?.load_id;
    
    if (!loadId) {
        return <p className="text-zinc-500 text-center py-4">Select a load to use negotiation tools</p>;
    }

    return (
        <div className="space-y-4">
            {/* DISPATCH DECISION - Top Priority */}
            <DispatchDecisionPanel loadId={loadId} load={load} />
            
            {/* Broker Vetting Alert */}
            <BrokerVettingAlert 
                brokerId={load?.broker_id} 
                brokerName={load?.broker_name} 
            />

            {/* Green Line Calculator */}
            <GreenLineCalculator 
                loadId={loadId}
                currentRate={load?.gross_pay}
                distance={load?.distance}
            />

            {/* Negotiation Script Generator */}
            <NegotiationScriptGenerator loadId={loadId} />

            {/* Lane Dossier */}
            <LaneDossier load={load} />

            {/* Backhaul Suggestions */}
            <BackhaulSuggestions loadId={loadId} />
        </div>
    );
}

// ==================== END NEGOTIATION BRAIN ====================

// Negotiation Assistant Panel
function NegotiationAssistant({ loadId, onRefresh }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchAssistant = useCallback(async () => {
        if (!loadId) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/operations/negotiation-assistant/${loadId}`, {
                credentials: 'include'
            });
            if (res.ok) {
                setData(await res.json());
            }
        } catch (e) {
            console.error('Failed to fetch negotiation assistant:', e);
        } finally {
            setLoading(false);
        }
    }, [loadId]);

    useEffect(() => {
        fetchAssistant();
    }, [fetchAssistant]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
        );
    }

    if (!data) {
        return <p className="text-zinc-500 text-center py-4">No negotiation data available</p>;
    }

    return (
        <div className="space-y-4">
            {/* Rate Suggestions */}
            <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                    <p className="text-green-400 text-xs mb-1">Suggested Start</p>
                    <p className="text-green-400 text-xl font-bold">{formatCurrency(data.suggested_starting_rate)}</p>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 text-center">
                    <p className="text-blue-400 text-xs mb-1">Market Estimate</p>
                    <p className="text-blue-400 text-xl font-bold">{formatCurrency(data.market_rate_estimate)}</p>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center">
                    <p className="text-amber-400 text-xs mb-1">Minimum Acceptable</p>
                    <p className="text-amber-400 text-xl font-bold">{formatCurrency(data.minimum_acceptable_rate)}</p>
                </div>
            </div>

            {/* Talking Points */}
            <div>
                <h4 className="text-zinc-300 font-medium mb-2 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-blue-500" />
                    Talking Points
                </h4>
                <ul className="space-y-2">
                    {data.talking_points?.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-zinc-400">
                            <Zap className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            {point}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}


// Live Call Caption Panel - Records broker calls and shows live transcription
function LiveCallCaptionPanel({ loadId, brokerPhone, brokerName, onTranscriptSaved }) {
    const [isRecording, setIsRecording] = useState(false);
    const [transcript, setTranscript] = useState([]);
    const [savedTranscript, setSavedTranscript] = useState(null);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [audioChunks, setAudioChunks] = useState([]);
    const [callStartTime, setCallStartTime] = useState(null);
    const [saving, setSaving] = useState(false);
    const transcriptRef = useRef(null);
    
    // Auto-scroll transcript
    useEffect(() => {
        if (transcriptRef.current) {
            transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
        }
    }, [transcript]);
    
    // Load existing transcript
    useEffect(() => {
        const loadTranscript = async () => {
            try {
                const res = await fetch(`${API_URL}/api/operations/call/${loadId}/transcript`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.transcript) {
                        setSavedTranscript(data.transcript);
                    }
                }
            } catch (e) {
                console.error('Failed to load transcript:', e);
            }
        };
        if (loadId) loadTranscript();
    }, [loadId]);
    
    // Start recording
    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
            const chunks = [];
            
            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                    setAudioChunks([...chunks]);
                }
            };
            
            recorder.onstop = () => {
                stream.getTracks().forEach(track => track.stop());
            };
            
            recorder.start(1000); // Capture in 1s chunks
            setMediaRecorder(recorder);
            setIsRecording(true);
            setCallStartTime(new Date());
            setTranscript([]);
            
            // Add initial message
            addTranscriptEntry('system', 'Call recording started...');
            
            toast.success('Recording started');
        } catch (e) {
            console.error('Failed to start recording:', e);
            toast.error('Microphone access denied. Please allow microphone access.');
        }
    };
    
    // Stop recording
    const stopRecording = async () => {
        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.stop();
        }
        setIsRecording(false);
        
        const duration = callStartTime ? Math.round((new Date() - callStartTime) / 1000) : 0;
        addTranscriptEntry('system', `Call ended. Duration: ${Math.floor(duration / 60)}m ${duration % 60}s`);
        
        toast.info('Recording stopped. Save the transcript when ready.');
    };
    
    // Add transcript entry
    const addTranscriptEntry = (speaker, text) => {
        setTranscript(prev => [...prev, {
            speaker,
            text,
            timestamp: new Date().toLocaleTimeString()
        }]);
    };
    
    // Manual entry for dispatcher/broker
    const [manualText, setManualText] = useState('');
    const [manualSpeaker, setManualSpeaker] = useState('dispatcher');
    
    const addManualEntry = () => {
        if (!manualText.trim()) return;
        addTranscriptEntry(manualSpeaker, manualText.trim());
        setManualText('');
    };
    
    // Save transcript and audio
    const saveCall = async () => {
        setSaving(true);
        try {
            // Format transcript
            const formattedTranscript = transcript
                .filter(t => t.speaker !== 'system')
                .map(t => `${t.speaker === 'broker' ? 'Broker' : 'Dispatcher'}:\n${t.text}`)
                .join('\n\n');
            
            // Save transcript
            const res = await fetch(`${API_URL}/api/operations/call/${loadId}/save-transcript`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    transcript: formattedTranscript,
                    call_duration_seconds: callStartTime ? Math.round((new Date() - callStartTime) / 1000) : null,
                    broker_phone: brokerPhone
                })
            });
            
            if (!res.ok) {
                throw new Error('Failed to save transcript');
            }
            
            // Save audio if we have chunks
            if (audioChunks.length > 0) {
                const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
                const formData = new FormData();
                formData.append('file', audioBlob, `call_${loadId}.webm`);
                
                await fetch(`${API_URL}/api/operations/call/${loadId}/save-recording`, {
                    method: 'POST',
                    credentials: 'include',
                    body: formData
                });
            }
            
            setSavedTranscript(formattedTranscript);
            toast.success('Call data saved successfully');
            onTranscriptSaved?.();
        } catch (e) {
            console.error('Failed to save call:', e);
            toast.error('Failed to save call data');
        } finally {
            setSaving(false);
        }
    };
    
    // Initiate phone call (mobile)
    const initiateCall = () => {
        if (brokerPhone) {
            // Start recording first
            startRecording();
            // Open phone dialer
            window.location.href = `tel:${brokerPhone}`;
        }
    };
    
    return (
        <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Phone className="w-4 h-4 text-blue-500" />
                        Broker Call
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        {isRecording && (
                            <span className="flex items-center gap-1 text-red-500 text-xs animate-pulse">
                                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                Recording
                            </span>
                        )}
                        <Button
                            size="sm"
                            variant={isRecording ? "destructive" : "outline"}
                            onClick={isRecording ? stopRecording : startRecording}
                            className={isRecording ? "" : "border-zinc-600"}
                        >
                            <Mic className={`w-4 h-4 ${isRecording ? 'animate-pulse' : ''}`} />
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-3">
                {/* Call Broker Button */}
                {brokerPhone && (
                    <Button
                        onClick={initiateCall}
                        className="w-full bg-green-600 hover:bg-green-700"
                        disabled={isRecording}
                    >
                        <Phone className="w-4 h-4 mr-2" />
                        Call {brokerName || 'Broker'}: {brokerPhone}
                    </Button>
                )}
                
                {/* Live Caption Area */}
                <div 
                    ref={transcriptRef}
                    className="bg-zinc-950 rounded-lg p-3 h-48 overflow-y-auto border border-zinc-800"
                >
                    {savedTranscript && !transcript.length ? (
                        <div className="space-y-2">
                            <p className="text-zinc-500 text-xs mb-2">Previous call transcript:</p>
                            <pre className="text-zinc-300 text-sm whitespace-pre-wrap font-sans">
                                {savedTranscript}
                            </pre>
                        </div>
                    ) : transcript.length > 0 ? (
                        <div className="space-y-3">
                            {transcript.map((entry, idx) => (
                                <div key={idx} className={`${
                                    entry.speaker === 'system' ? 'text-center' :
                                    entry.speaker === 'broker' ? 'text-left' : 'text-right'
                                }`}>
                                    {entry.speaker === 'system' ? (
                                        <span className="text-zinc-500 text-xs italic">{entry.text}</span>
                                    ) : (
                                        <div className={`inline-block max-w-[80%] rounded-lg p-2 ${
                                            entry.speaker === 'broker' 
                                                ? 'bg-blue-500/20 text-blue-100' 
                                                : 'bg-green-500/20 text-green-100'
                                        }`}>
                                            <p className="text-xs text-zinc-400 mb-1">
                                                {entry.speaker === 'broker' ? 'Broker' : 'Dispatcher'}
                                            </p>
                                            <p className="text-sm">{entry.text}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-zinc-500">
                            <MessageSquare className="w-8 h-8 mb-2 opacity-50" />
                            <p className="text-sm">Call captions will appear here</p>
                            <p className="text-xs mt-1">Tap the phone button to start</p>
                        </div>
                    )}
                </div>
                
                {/* Manual Entry (when recording) */}
                {isRecording && (
                    <div className="flex gap-2">
                        <select
                            value={manualSpeaker}
                            onChange={(e) => setManualSpeaker(e.target.value)}
                            className="bg-zinc-800 border-zinc-700 rounded-md px-2 text-sm text-white"
                        >
                            <option value="dispatcher">Me</option>
                            <option value="broker">Broker</option>
                        </select>
                        <Input
                            value={manualText}
                            onChange={(e) => setManualText(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && addManualEntry()}
                            placeholder="Type what was said..."
                            className="flex-1 bg-zinc-800 border-zinc-700 text-white"
                        />
                        <Button size="sm" onClick={addManualEntry} className="bg-blue-600">
                            Add
                        </Button>
                    </div>
                )}
                
                {/* Save Button */}
                {transcript.length > 0 && !isRecording && (
                    <Button
                        onClick={saveCall}
                        disabled={saving}
                        className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                        {saving ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                        )}
                        Save Call Transcript
                    </Button>
                )}
            </CardContent>
        </Card>
    );
}


// ==================== LIVE CALL ASSISTANT ====================
// Real-time AI negotiation assistance during broker calls

function LiveCallAssistant({ loadId, onSuggestionUsed }) {
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [quickResponse, setQuickResponse] = useState('');
    const [callContext, setCallContext] = useState(null);
    const [contextLoading, setContextLoading] = useState(true);
    const [history, setHistory] = useState([]);
    const inputRef = useRef(null);
    
    // Fetch call context on mount
    useEffect(() => {
        const fetchContext = async () => {
            setContextLoading(true);
            try {
                const res = await fetch(`${API_URL}/api/operations/call/${loadId}/context`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    const data = await res.json();
                    setCallContext(data);
                    // Set initial suggestions
                    if (data.ready_suggestions) {
                        setSuggestions(data.ready_suggestions);
                    }
                }
            } catch (e) {
                console.error('Failed to fetch call context:', e);
            } finally {
                setContextLoading(false);
            }
        };
        
        if (loadId) fetchContext();
    }, [loadId]);
    
    // Handle input submission
    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!input.trim() || loading) return;
        
        const userInput = input.trim();
        setInput('');
        setLoading(true);
        
        // Add to history
        setHistory(prev => [...prev, { type: 'user', text: userInput, timestamp: new Date() }]);
        
        try {
            const res = await fetch(`${API_URL}/api/operations/call/${loadId}/assist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ user_input: userInput })
            });
            
            if (res.ok) {
                const data = await res.json();
                setQuickResponse(data.quick_response || '');
                setSuggestions(data.suggestions || []);
                
                // Add response to history
                setHistory(prev => [...prev, { 
                    type: 'ai', 
                    text: data.quick_response, 
                    suggestions: data.suggestions,
                    timestamp: new Date() 
                }]);
            } else {
                toast.error('Failed to get suggestion');
            }
        } catch (e) {
            console.error('AI assist error:', e);
            toast.error('Failed to get AI suggestion');
        } finally {
            setLoading(false);
            inputRef.current?.focus();
        }
    };
    
    // Quick input shortcuts
    const quickInputs = [
        { label: 'Offered $', action: () => setInput('Broker offered $') },
        { label: 'Counter?', action: () => setInput('What should I counter with?') },
        { label: 'Too low', action: () => setInput('Rate is too low') },
        { label: 'Accept?', action: () => setInput('Should I accept this rate?') }
    ];
    
    // Get suggestion icon and color
    const getSuggestionStyle = (type) => {
        switch (type) {
            case 'counter_offer':
                return { icon: '💰', bg: 'bg-green-500/20', border: 'border-green-500/30', text: 'text-green-400' };
            case 'talking_point':
                return { icon: '💡', bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400' };
            case 'warning':
                return { icon: '⚠️', bg: 'bg-amber-500/20', border: 'border-amber-500/30', text: 'text-amber-400' };
            case 'insight':
                return { icon: '📊', bg: 'bg-purple-500/20', border: 'border-purple-500/30', text: 'text-purple-400' };
            case 'action':
                return { icon: '🎯', bg: 'bg-cyan-500/20', border: 'border-cyan-500/30', text: 'text-cyan-400' };
            default:
                return { icon: '💬', bg: 'bg-zinc-500/20', border: 'border-zinc-500/30', text: 'text-zinc-400' };
        }
    };
    
    if (contextLoading) {
        return (
            <Card className="bg-zinc-900/50 border-zinc-800 border-2 border-purple-500/30">
                <CardContent className="p-4 flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-purple-500 mr-2" />
                    <span className="text-zinc-400">Loading call assistant...</span>
                </CardContent>
            </Card>
        );
    }
    
    return (
        <Card className="bg-zinc-900/50 border-zinc-800 border-2 border-purple-500/30" data-testid="live-call-assistant">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Zap className="w-4 h-4 text-purple-500" />
                        Live Call Assistant
                        <Badge variant="outline" className="text-xs border-purple-500/50 text-purple-400">AI</Badge>
                    </CardTitle>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Quick Reference Numbers */}
                {callContext?.quick_reference && (
                    <div className="grid grid-cols-4 gap-2 p-3 bg-zinc-950 rounded-lg border border-zinc-800">
                        <div className="text-center">
                            <p className="text-zinc-500 text-xs">ASK</p>
                            <p className="text-green-400 font-bold">{callContext.quick_reference.opening_ask}</p>
                        </div>
                        <div className="text-center">
                            <p className="text-zinc-500 text-xs">TARGET</p>
                            <p className="text-blue-400 font-bold">{callContext.quick_reference.target}</p>
                        </div>
                        <div className="text-center">
                            <p className="text-zinc-500 text-xs">FLOOR</p>
                            <p className="text-amber-400 font-bold">{callContext.quick_reference.floor}</p>
                        </div>
                        <div className="text-center">
                            <p className="text-zinc-500 text-xs">MIN</p>
                            <p className="text-red-400 font-bold">{callContext.quick_reference.green_line}</p>
                        </div>
                    </div>
                )}
                
                {/* Broker Warning */}
                {callContext?.broker_warning && (
                    <div className="p-2 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <span className="text-red-400 text-sm font-medium">High-risk broker - verify payment terms</span>
                    </div>
                )}
                
                {/* Quick Response Display */}
                {quickResponse && (
                    <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg animate-pulse-once">
                        <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                                <Zap className="w-4 h-4 text-purple-400" />
                            </div>
                            <div>
                                <p className="text-purple-300 font-semibold text-lg">{quickResponse}</p>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Suggestions Panel */}
                {suggestions.length > 0 && (
                    <div className="space-y-2">
                        {suggestions.map((suggestion, idx) => {
                            const style = getSuggestionStyle(suggestion.type);
                            return (
                                <div 
                                    key={idx}
                                    className={`p-3 rounded-lg border ${style.bg} ${style.border} cursor-pointer hover:opacity-80 transition-opacity`}
                                    onClick={() => {
                                        navigator.clipboard?.writeText(suggestion.text);
                                        toast.success('Copied to clipboard');
                                        onSuggestionUsed?.(suggestion);
                                    }}
                                    data-testid={`suggestion-${suggestion.type}`}
                                >
                                    <div className="flex items-start gap-2">
                                        <span className="text-lg">{style.icon}</span>
                                        <div className="flex-1">
                                            <p className={`font-medium ${style.text}`}>{suggestion.text}</p>
                                            {suggestion.reason && (
                                                <p className="text-zinc-500 text-xs mt-1">{suggestion.reason}</p>
                                            )}
                                        </div>
                                        <Copy className="w-4 h-4 text-zinc-500 opacity-0 group-hover:opacity-100" />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                
                {/* Quick Input Shortcuts */}
                <div className="flex gap-2 flex-wrap">
                    {quickInputs.map((item, idx) => (
                        <Button
                            key={idx}
                            variant="outline"
                            size="sm"
                            onClick={item.action}
                            className="text-xs border-zinc-700 hover:bg-zinc-800"
                        >
                            {item.label}
                        </Button>
                    ))}
                </div>
                
                {/* Input Field */}
                <form onSubmit={handleSubmit} className="flex gap-2">
                    <Input
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Type what broker said... (e.g., 'Offered $2,500')"
                        className="flex-1 bg-zinc-800 border-zinc-700 text-white"
                        disabled={loading}
                        data-testid="call-assistant-input"
                    />
                    <Button 
                        type="submit" 
                        disabled={loading || !input.trim()}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="call-assistant-submit"
                    >
                        {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Send className="w-4 h-4" />
                        )}
                    </Button>
                </form>
                
                {/* Speed indicator */}
                <div className="flex items-center justify-center gap-2 text-zinc-500 text-xs">
                    <Zap className="w-3 h-3" />
                    <span>AI responds in 1-2 seconds</span>
                </div>
            </CardContent>
        </Card>
    );
}


// Rate Confirmation Workflow Panel
function RateConfirmationPanel({ 
    item, 
    loadId,
    onVerify, 
    onRefresh,
    verifying 
}) {
    const [uploading, setUploading] = useState(false);
    const [signing, setSigning] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [aiVerifying, setAiVerifying] = useState(false);
    const [rateConStatus, setRateConStatus] = useState(null);
    const [signaturePosition, setSignaturePosition] = useState('bottom-right');
    const [verificationResult, setVerificationResult] = useState(null);
    const [wasRemoved, setWasRemoved] = useState(false);
    const [isViewerExpanded, setIsViewerExpanded] = useState(false);
    const fileInputRef = useRef(null);
    const replaceInputRef = useRef(null);
    
    // Allowed file types for rate confirmation
    const ALLOWED_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,.webp';
    
    // Track rate confirmation status - wasRemoved overrides item state until parent refreshes
    const isUploaded = wasRemoved ? false : (rateConStatus?.uploaded ?? !!item?.rate_confirmation_file);
    const verificationStatus = wasRemoved ? 'pending' : (rateConStatus?.verification_status || item?.rate_confirmation_status || 'pending');
    const isSigned = wasRemoved ? false : (rateConStatus?.signed ?? item?.rate_confirmation_signed);
    const extractedText = rateConStatus?.extracted_text || '';
    const hasExtractedText = rateConStatus?.has_extracted_text || false;
    const fileType = rateConStatus?.file_type || 'pdf';
    const isImageFile = fileType === 'image' || ['png', 'jpg', 'jpeg', 'webp'].some(ext => 
        (rateConStatus?.original_filename || '').toLowerCase().endsWith(ext)
    );
    
    // AI verification details
    const aiVerification = rateConStatus?.ai_verification || {};
    const canBook = rateConStatus?.can_book || false;
    const bookingBlockedReason = rateConStatus?.booking_blocked_reason;
    const requiresOverride = rateConStatus?.requires_override || false;
    
    // Fetch current status and extracted text
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const res = await fetch(`${API_URL}/api/operations/rate-con/${loadId}/status`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    const data = await res.json();
                    setRateConStatus(data);
                    if (data.default_signature_position) {
                        setSignaturePosition(data.default_signature_position);
                    }
                    if (data.verification_result || data.ai_verification) {
                        setVerificationResult(data.verification_result || data.ai_verification);
                    }
                }
            } catch (e) {
                console.error('Failed to fetch rate con status:', e);
            }
        };
        if (loadId) fetchStatus();
    }, [loadId]);
    
    // Handle file upload (PDF or image)
    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.webp'];
        const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
        if (!allowedExtensions.includes(fileExt)) {
            toast.error('Only PDF or image files (PNG, JPG, JPEG, WEBP) are accepted');
            return;
        }
        
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            // auto_verify=true triggers AI verification after upload
            const res = await fetch(`${API_URL}/api/operations/rate-con/${loadId}/upload?auto_verify=true`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if (res.ok) {
                const data = await res.json();
                toast.success('Rate confirmation uploaded');
                setWasRemoved(false);
                // Refetch status to get extracted text and AI verification result
                const statusRes = await fetch(`${API_URL}/api/operations/rate-con/${loadId}/status`, {
                    credentials: 'include'
                });
                if (statusRes.ok) {
                    const statusData = await statusRes.json();
                    setRateConStatus(statusData);
                    setVerificationResult(statusData.ai_verification || statusData.verification_result);
                }
                onRefresh?.();
                
                // Show AI verification result if available
                if (data.verification_result) {
                    const vr = data.verification_result;
                    if (vr.verification_status === 'verified' || vr.verification_status === 'verified_with_warnings') {
                        toast.success('AI Verification: All fields match!', { duration: 4000 });
                    } else if (vr.verification_status === 'mismatch') {
                        const issues = vr.discrepancies?.map(d => d.field).join(', ') || 'fields';
                        toast.error(`AI Verification: Mismatch detected in ${issues}`, { duration: 5000 });
                    } else if (vr.verification_status === 'missing_data') {
                        toast.warning('AI Verification: Required data missing from document', { duration: 4000 });
                    } else if (vr.verification_status === 'failed') {
                        toast.info('AI Verification unavailable - manual verification required');
                    }
                } else if (data.text_extracted) {
                    toast.info('Text extracted from PDF - ready for verification');
                } else if (data.extraction_error) {
                    toast.warning('Could not extract text - AI verification will analyze the document');
                }
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Upload failed');
            }
        } catch (e) {
            toast.error('Failed to upload file');
        } finally {
            setUploading(false);
        }
    };
    
    // Handle replace upload
    const handleReplaceUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.webp'];
        const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
        if (!allowedExtensions.includes(fileExt)) {
            toast.error('Only PDF or image files (PNG, JPG, JPEG, WEBP) are accepted');
            return;
        }
        
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            // Use upload endpoint with auto_verify (replace endpoint will be enhanced)
            const res = await fetch(`${API_URL}/api/operations/rate-con/${loadId}/upload?auto_verify=true`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if (res.ok) {
                const data = await res.json();
                toast.success('Rate confirmation replaced');
                // Refetch status to get new extracted text and AI verification
                const statusRes = await fetch(`${API_URL}/api/operations/rate-con/${loadId}/status`, {
                    credentials: 'include'
                });
                if (statusRes.ok) {
                    const statusData = await statusRes.json();
                    setRateConStatus(statusData);
                    setVerificationResult(statusData.ai_verification || statusData.verification_result);
                }
                onRefresh?.();
                
                // Show AI verification result
                if (data.verification_result) {
                    const vr = data.verification_result;
                    if (vr.verification_status === 'verified' || vr.verification_status === 'verified_with_warnings') {
                        toast.success('AI Verification: All fields match!');
                    } else if (vr.verification_status === 'mismatch') {
                        toast.error('AI Verification: Mismatch detected - review before booking');
                    }
                }
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Replace failed');
            }
        } catch (e) {
            toast.error('Failed to replace file');
        } finally {
            setUploading(false);
        }
    };
    
    // Handle remove
    const handleRemove = async () => {
        if (!window.confirm('Remove this rate confirmation? This will reset the workflow.')) return;
        
        setRemoving(true);
        try {
            const res = await fetch(`${API_URL}/api/operations/rate-con/${loadId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            
            if (res.ok) {
                toast.success('Rate confirmation removed');
                setWasRemoved(true);
                setRateConStatus({
                    uploaded: false,
                    signed: false,
                    has_extracted_text: false,
                    extracted_text: '',
                    original_filename: null,
                    verification_status: 'pending'
                });
                setVerificationResult(null);
                onRefresh?.();
            } else {
                const err = await res.json().catch(() => ({}));
                toast.error(err.detail || 'Failed to remove');
            }
        } catch (e) {
            console.error('Remove error:', e);
            toast.error('Failed to remove');
        } finally {
            setRemoving(false);
        }
    };
    
    // Handle verification using extracted OCR text
    const handleVerify = async () => {
        if (!hasExtractedText) {
            toast.error('No extracted text available for verification');
            return;
        }
        
        try {
            const res = await fetch(`${API_URL}/api/operations/queue/${loadId}/verify-rate-confirmation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ 
                    rate_confirmation_text: null, // Use stored extracted text
                    call_transcript: null 
                })
            });
            if (res.ok) {
                const data = await res.json();
                setVerificationResult(data);
                if (data.status === 'verified') {
                    toast.success('Rate confirmation verified!');
                } else {
                    toast.warning('Discrepancies detected - review before signing');
                }
                onRefresh?.();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Verification failed');
            }
        } catch (e) {
            toast.error('Verification failed');
        }
    };
    
    // Handle AI-powered verification (Gemini Vision)
    const handleAiVerify = async () => {
        setAiVerifying(true);
        try {
            const res = await fetch(`${API_URL}/api/operations/queue/${loadId}/ai-verify`, {
                method: 'POST',
                credentials: 'include'
            });
            
            if (res.ok) {
                const data = await res.json();
                
                // Refetch full status
                const statusRes = await fetch(`${API_URL}/api/operations/rate-con/${loadId}/status`, {
                    credentials: 'include'
                });
                if (statusRes.ok) {
                    const statusData = await statusRes.json();
                    setRateConStatus(statusData);
                    setVerificationResult(statusData.ai_verification);
                }
                
                // Show result toast
                if (data.verification_status === 'verified' || data.verification_status === 'verified_with_warnings') {
                    toast.success('AI Verification: All fields match!', { 
                        description: data.message,
                        duration: 4000 
                    });
                } else if (data.verification_status === 'mismatch') {
                    const issues = data.discrepancies?.map(d => d.field).join(', ') || 'fields';
                    toast.error(`AI Verification: Mismatch in ${issues}`, { 
                        description: 'Review discrepancies below before booking',
                        duration: 5000 
                    });
                } else if (data.verification_status === 'missing_data') {
                    toast.warning('AI Verification: Missing required data', {
                        description: data.message,
                        duration: 4000
                    });
                } else {
                    toast.error('AI Verification failed', {
                        description: data.message || 'Please try again or use manual verification'
                    });
                }
                
                onRefresh?.();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'AI Verification failed');
            }
        } catch (e) {
            console.error('AI verification error:', e);
            toast.error('AI Verification failed - network error');
        } finally {
            setAiVerifying(false);
        }
    };
    
    // Handle signing with position
    const handleSign = async () => {
        setSigning(true);
        try {
            const res = await fetch(`${API_URL}/api/operations/rate-con/${loadId}/sign`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ position: signaturePosition })
            });
            
            if (res.ok) {
                const data = await res.json();
                toast.success('Rate confirmation signed!');
                onRefresh?.();
                setRateConStatus(prev => ({ ...prev, signed: true, signed_at: data.signed_at }));
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Signing failed');
            }
        } catch (e) {
            toast.error('Failed to sign document');
        } finally {
            setSigning(false);
        }
    };
    
    // Download signed PDF using fetch + blob
    const handleDownloadSigned = async () => {
        const getSessionToken = () => {
            const cookies = document.cookie.split(';');
            for (let cookie of cookies) {
                const [name, value] = cookie.trim().split('=');
                if (name === 'session_token') return value;
            }
            return null;
        };
        
        const token = getSessionToken();
        const url = `${API_URL}/api/operations/rate-con/${loadId}/download-signed${token ? `?token=${token}` : ''}`;
        
        try {
            const res = await fetch(url, { credentials: 'include' });
            if (!res.ok) {
                toast.error('Failed to download');
                return;
            }
            
            const blob = await res.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `rate-con-signed-${loadId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
            toast.success('Download started');
        } catch (e) {
            console.error('Download error:', e);
            toast.error('Failed to download');
        }
    };
    
    // Get comparison data from AI verification or legacy verification
    const aiFieldResults = rateConStatus?.ai_verification?.field_results || [];
    const aiDiscrepancies = rateConStatus?.ai_verification?.discrepancies || [];
    const aiWarnings = rateConStatus?.ai_verification?.warnings || [];
    const comparison = aiFieldResults.length > 0 ? aiFieldResults : 
                       (verificationResult?.comparison || rateConStatus?.comparison || item?.verification_result?.comparison || []);
    const discrepancies = aiDiscrepancies.length > 0 ? aiDiscrepancies :
                          (verificationResult?.discrepancies || item?.verification_result?.discrepancies || []);
    
    return (
        <Card className="bg-zinc-900/50 border-zinc-800">
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                    <FileCheck className="w-4 h-4 text-green-500" />
                    Rate Confirmation Workflow
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-4">
                    {/* Upload Panel with OCR Viewer */}
                    <div className="space-y-3">
                        {/* Upload Container / OCR Viewer */}
                        <div className={`rounded-lg border-2 transition-all ${
                            isUploaded 
                                ? 'border-green-500/50 bg-green-500/5' 
                                : 'border-dashed border-zinc-700 bg-zinc-800/50'
                        }`}>
                            {/* Header - always visible */}
                            <div 
                                onClick={!isUploaded && !uploading ? () => fileInputRef.current?.click() : undefined}
                                className={`p-4 ${!isUploaded ? 'cursor-pointer hover:bg-zinc-700/30' : ''}`}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    data-testid="ratecon-file-input"
                                />
                                
                                {isUploaded ? (
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <CheckCircle className="w-6 h-6 text-green-500" />
                                            <div>
                                                <p className="text-green-400 text-sm font-medium">Rate Confirmation Uploaded</p>
                                                <p className="text-zinc-500 text-xs flex items-center gap-1">
                                                    {rateConStatus?.original_filename || item?.rate_confirmation_original_filename || 'ratecon.pdf'}
                                                    {isImageFile && (
                                                        <Badge variant="outline" className="ml-2 text-xs border-blue-500/50 text-blue-400">
                                                            Image
                                                        </Badge>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        {hasExtractedText && (
                                            <button
                                                onClick={() => setIsViewerExpanded(!isViewerExpanded)}
                                                className="p-2 hover:bg-zinc-700/50 rounded-lg transition-colors"
                                                title={isViewerExpanded ? 'Collapse' : 'Expand'}
                                            >
                                                {isViewerExpanded ? (
                                                    <Minimize2 className="w-4 h-4 text-zinc-400" />
                                                ) : (
                                                    <Maximize2 className="w-4 h-4 text-zinc-400" />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <div className="text-center">
                                        {uploading ? (
                                            <Loader2 className="w-8 h-8 mx-auto text-blue-500 animate-spin mb-2" />
                                        ) : (
                                            <Upload className="w-8 h-8 mx-auto text-zinc-500 mb-2" />
                                        )}
                                        <p className="text-zinc-400 text-sm mb-1">
                                            {uploading ? 'Uploading & Running AI Verification...' : 'Upload Rate Confirmation'}
                                        </p>
                                        <p className="text-zinc-500 text-xs">PDF or Image (PNG, JPG) • Tap to select</p>
                                    </div>
                                )}
                            </div>
                            
                            {/* OCR Extracted Text Viewer */}
                            {isUploaded && hasExtractedText && (
                                <div className="border-t border-zinc-700/50">
                                    <div className="px-4 py-2 bg-zinc-800/50 flex items-center justify-between">
                                        <span className="text-zinc-400 text-xs font-medium flex items-center gap-1">
                                            <FileText className="w-3 h-3" />
                                            Extracted Rate Confirmation
                                        </span>
                                        <span className="text-zinc-500 text-xs">
                                            {extractedText.length} characters
                                        </span>
                                    </div>
                                    <div 
                                        className={`px-4 py-3 overflow-y-auto font-mono text-xs text-zinc-300 whitespace-pre-wrap transition-all ${
                                            isViewerExpanded 
                                                ? 'h-[70vh] max-h-[70vh]' 
                                                : 'h-[40vh] max-h-[40vh]'
                                        }`}
                                        style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
                                    >
                                        {extractedText || 'No text extracted'}
                                    </div>
                                </div>
                            )}
                            
                            {/* No text extracted warning */}
                            {isUploaded && !hasExtractedText && (
                                <div className="px-4 py-3 border-t border-zinc-700/50 bg-amber-500/5">
                                    <div className="flex items-center gap-2 text-amber-400 text-xs">
                                        <AlertTriangle className="w-4 h-4" />
                                        <span>Could not extract text from this PDF. The document may be scanned or image-based.</span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Replace / Remove Actions */}
                        {isUploaded && !isSigned && (
                            <div className="flex gap-2">
                                <input
                                    ref={replaceInputRef}
                                    type="file"
                                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                                    onChange={handleReplaceUpload}
                                    className="hidden"
                                />
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => replaceInputRef.current?.click()}
                                    disabled={uploading}
                                    className="flex-1 border-zinc-600 text-zinc-300"
                                >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Replace
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleRemove}
                                    disabled={removing}
                                    className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                                >
                                    {removing ? (
                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    ) : (
                                        <Trash2 className="w-3 h-3 mr-1" />
                                    )}
                                    Remove
                                </Button>
                            </div>
                        )}
                        
                        {/* AI Verification Button - Primary Method */}
                        {isUploaded && !isSigned && verificationStatus === 'pending' && (
                            <Button
                                onClick={handleAiVerify}
                                disabled={aiVerifying || uploading}
                                className="w-full bg-purple-600 hover:bg-purple-700"
                                data-testid="ai-verify-rate-confirmation"
                            >
                                {aiVerifying ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        AI Analyzing Document...
                                    </>
                                ) : (
                                    <>
                                        <Zap className="w-4 h-4 mr-2" />
                                        AI Verify Rate Confirmation
                                    </>
                                )}
                            </Button>
                        )}
                        
                        {/* Re-verify Button - Shows after initial verification */}
                        {isUploaded && !isSigned && verificationStatus !== 'pending' && (
                            <Button
                                onClick={handleAiVerify}
                                disabled={aiVerifying}
                                variant="outline"
                                className="w-full border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                                data-testid="re-verify-rate-confirmation"
                            >
                                {aiVerifying ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        Re-analyzing...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        Re-verify Document
                                    </>
                                )}
                            </Button>
                        )}
                    </div>
                    
                    {/* Verification Results Section */}
                    {isUploaded && !isSigned && (comparison.length > 0 || discrepancies.length > 0 || verificationStatus !== 'pending') && (
                        <div className="space-y-3 border-t border-zinc-700 pt-4">
                            {/* Status Badge */}
                            <div className={`rounded-lg p-3 text-center ${
                                verificationStatus === 'verified' || verificationStatus === 'verified_with_warnings' ? 'bg-green-500/10 border border-green-500/30' :
                                verificationStatus === 'mismatch' || verificationStatus === 'discrepancy' ? 'bg-red-500/10 border border-red-500/30' :
                                verificationStatus === 'missing_data' ? 'bg-amber-500/10 border border-amber-500/30' :
                                'bg-zinc-800/50 border border-zinc-700'
                            }`}>
                                {verificationStatus === 'verified' ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <CheckCircle className="w-5 h-5 text-green-500" />
                                        <span className="text-green-400 font-semibold">✓ Verified - Ready to Book</span>
                                    </div>
                                ) : verificationStatus === 'verified_with_warnings' ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <CheckCircle className="w-5 h-5 text-green-500" />
                                        <span className="text-green-400 font-semibold">✓ Verified (with warnings)</span>
                                    </div>
                                ) : verificationStatus === 'mismatch' || verificationStatus === 'discrepancy' ? (
                                    <div className="flex flex-col items-center gap-1">
                                        <div className="flex items-center gap-2">
                                            <XCircle className="w-5 h-5 text-red-500" />
                                            <span className="text-red-400 font-semibold">⚠️ Mismatch Detected</span>
                                        </div>
                                        <span className="text-zinc-400 text-xs">Review discrepancies below - Override available if correct</span>
                                    </div>
                                ) : verificationStatus === 'missing_data' ? (
                                    <div className="flex flex-col items-center gap-1">
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                                            <span className="text-amber-400 font-semibold">❌ Missing Required Data</span>
                                        </div>
                                        <span className="text-zinc-400 text-xs">Could not find all required fields in document</span>
                                    </div>
                                ) : verificationStatus === 'failed' ? (
                                    <div className="flex flex-col items-center gap-1">
                                        <div className="flex items-center gap-2">
                                            <XCircle className="w-5 h-5 text-zinc-500" />
                                            <span className="text-zinc-400 font-semibold">AI Verification Failed</span>
                                        </div>
                                        <span className="text-zinc-500 text-xs">Try again or use manual verification override</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center gap-2">
                                        <AlertTriangle className="w-5 h-5 text-zinc-500" />
                                        <span className="text-zinc-400 font-semibold">Pending Verification</span>
                                    </div>
                                )}
                            </div>
                            
                            {/* Comparison Table */}
                            {comparison && comparison.length > 0 && (
                                <div className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700">
                                    <p className="text-zinc-300 text-xs font-semibold mb-2 flex items-center gap-1">
                                        <FileText className="w-3 h-3" />
                                        Extracted Values Comparison
                                    </p>
                                    <div className="space-y-1">
                                        {comparison.map((row, idx) => (
                                            <div key={idx} className="grid grid-cols-3 gap-2 text-xs py-1 border-b border-zinc-700/50 last:border-0">
                                                <span className="text-zinc-500">{row.field}</span>
                                                <span className="text-zinc-300">{row.expected}</span>
                                                <span className={`${
                                                    row.status === 'match' ? 'text-green-400' :
                                                    row.status === 'mismatch' ? 'text-red-400' :
                                                    'text-zinc-500'
                                                }`}>
                                                    {row.found}
                                                    {row.status === 'match' && <CheckCircle className="w-3 h-3 inline ml-1" />}
                                                    {row.status === 'mismatch' && <XCircle className="w-3 h-3 inline ml-1" />}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Discrepancies */}
                            {discrepancies.length > 0 && (
                                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                                    <p className="text-red-400 text-xs font-semibold mb-1">Issues Found:</p>
                                    <ul className="space-y-1">
                                        {discrepancies.map((d, idx) => (
                                            <li key={idx} className="text-zinc-300 text-xs flex items-start gap-1">
                                                <AlertTriangle className="w-3 h-3 text-red-500 flex-shrink-0 mt-0.5" />
                                                {d.issue}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Sign Section */}
                    {isUploaded && !isSigned && (
                        <div className="border-t border-zinc-700 pt-4">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {/* Signature Position */}
                                <div className="space-y-2">
                                    <p className="text-zinc-400 text-xs font-medium">Signature Position</p>
                                    <div className="flex gap-2 flex-wrap">
                                        {[
                                            { value: 'bottom-left', label: 'Bottom Left' },
                                            { value: 'bottom-center', label: 'Bottom Center' },
                                            { value: 'bottom-right', label: 'Bottom Right' }
                                        ].map(opt => (
                                            <Button
                                                key={opt.value}
                                                variant={signaturePosition === opt.value ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setSignaturePosition(opt.value)}
                                                className={signaturePosition === opt.value 
                                                    ? 'bg-amber-600 hover:bg-amber-700' 
                                                    : 'border-zinc-600 text-zinc-300'
                                                }
                                            >
                                                {opt.label}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Sign Button */}
                                <div className="flex items-end">
                                    <Button
                                        onClick={handleSign}
                                        disabled={signing}
                                        className="w-full h-12 text-lg bg-amber-600 hover:bg-amber-700"
                                        data-testid="sign-rate-con"
                                    >
                                        {signing ? (
                                            <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                        ) : (
                                            <PenTool className="w-5 h-5 mr-2" />
                                        )}
                                        Sign Rate Confirmation
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Signed State - Download */}
                    {isSigned && (
                        <div className="border-t border-zinc-700 pt-4">
                            {/* Signed Confirmation */}
                            <div className="rounded-lg p-4 text-center border-2 border-green-500/50 bg-green-500/5 mb-3">
                                <CheckCircle className="w-10 h-10 mx-auto text-green-500 mb-2" />
                                <p className="text-green-400 font-semibold">Document Signed</p>
                                <p className="text-zinc-400 text-xs mt-1">
                                    Ready to download and send to broker
                                </p>
                            </div>
                            
                            {/* Download Button */}
                            <Button
                                onClick={handleDownloadSigned}
                                className="w-full bg-green-600 hover:bg-green-700"
                                data-testid="download-signed-ratecon"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                Download Signed Rate Confirmation
                            </Button>
                            
                            {/* Workflow Complete */}
                            <div className="mt-3 bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-center">
                                <p className="text-green-400 text-sm">
                                    Download and send via email, then book the load below.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}


// Active Load Panel
function ActiveLoadPanel({ 
    item, 
    onBook, 
    onRemove, 
    onVerify,
    onRefresh,
    bookingLoading,
    verifying
}) {
    const [callTranscript, setCallTranscript] = useState('');
    const [brokerRisk, setBrokerRisk] = useState(null);
    
    const load = item?.load || {};
    const truck = item?.truck || {};
    const broker = item?.broker || {};
    const hosState = item?.hos_state || {};
    
    // Calculate ETAs
    const deadheadMiles = load.original_deadhead_miles || 50;
    const loadMiles = load.distance || 0;
    const pickupETA = calculateETA(deadheadMiles, hosState);
    const deliveryETA = calculateETA(deadheadMiles + loadMiles, hosState);
    
    // Verification status
    const verificationStatus = item?.rate_confirmation_status || 'pending';
    
    // Check if rate confirmation is signed (enables booking)
    const isSigned = item?.rate_confirmation_signed || false;
    
    // Check if rate confirmation is uploaded
    const hasRateCon = item?.rate_confirmation_file || false;
    
    // Broker risk status
    const brokerRiskLevel = brokerRisk?.risk_level || 'caution';
    const brokerRiskScore = brokerRisk?.risk_score || 70;
    const brokerIsHighRisk = brokerRiskLevel === 'high';
    
    // Booking allowed when:
    // 1. Rate confirmation is signed (signing implies manual verification), OR
    // 2. AI verification passed (verified or verified_with_warnings)
    // AND
    // 3. Broker is not HIGH RISK (or user uses override)
    const verifiedStatuses = ['verified', 'verified_with_warnings'];
    const needsOverride = ['mismatch', 'discrepancy', 'missing_data', 'failed'];
    
    const rateConVerified = isSigned || verifiedStatuses.includes(verificationStatus);
    const canBook = rateConVerified && !brokerIsHighRisk;
    const canOverrideBook = needsOverride.includes(verificationStatus) || brokerIsHighRisk;
    
    // Determine booking blocked reason
    let bookingBlockedReason = null;
    if (!hasRateCon) {
        bookingBlockedReason = 'Upload rate confirmation first';
    } else if (verificationStatus === 'pending') {
        bookingBlockedReason = 'Run AI verification first';
    } else if (needsOverride.includes(verificationStatus) && !isSigned) {
        bookingBlockedReason = 'Verification failed - override required';
    } else if (brokerIsHighRisk) {
        bookingBlockedReason = 'High-risk broker - override required';
    }
    
    // Handler for broker risk assessment callback
    const handleRiskAssessed = useCallback((riskData) => {
        setBrokerRisk(riskData);
    }, []);
    
    // Rate per mile
    const ratePerMile = loadMiles > 0 ? (load.gross_pay / loadMiles).toFixed(2) : '--';

    return (
        <div className="space-y-6">
            {/* Broker Risk Analysis - Top Priority Warning */}
            <BrokerRiskAnalysis 
                loadId={load.load_id} 
                onRiskAssessed={handleRiskAssessed}
            />
            
            {/* Main Info Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Load Information */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <FileText className="w-4 h-4 text-blue-500" />
                            Load Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Load ID</span>
                            <span className="text-white font-mono">{load.load_id?.slice(-8)}</span>
                        </div>
                        <div className="bg-zinc-800/50 rounded p-2">
                            <div className="flex items-center gap-2 text-green-400 text-xs mb-1">
                                <MapPin className="w-3 h-3" />
                                PICKUP
                            </div>
                            <p className="text-white font-medium">{load.origin_city}, {load.origin_state}</p>
                            <p className="text-zinc-400 text-sm">{load.pickup_date} {load.pickup_time}</p>
                        </div>
                        <div className="bg-zinc-800/50 rounded p-2">
                            <div className="flex items-center gap-2 text-blue-400 text-xs mb-1">
                                <MapPin className="w-3 h-3" />
                                DELIVERY
                            </div>
                            <p className="text-white font-medium">{load.destination_city}, {load.destination_state}</p>
                            <p className="text-zinc-400 text-sm">{load.delivery_date} {load.delivery_time}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-700">
                            <div>
                                <p className="text-zinc-500 text-xs">Distance</p>
                                <p className="text-white font-medium">{loadMiles} mi</p>
                            </div>
                            <div>
                                <p className="text-zinc-500 text-xs">Rate/Mile</p>
                                <p className="text-green-400 font-medium">${ratePerMile}</p>
                            </div>
                        </div>
                        <div className="bg-green-500/10 border border-green-500/30 rounded p-3 text-center">
                            <p className="text-green-400 text-xs">RATE</p>
                            <p className="text-green-400 text-2xl font-bold">{formatCurrency(load.gross_pay)}</p>
                        </div>
                    </CardContent>
                </Card>

                {/* Broker Information */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-purple-500" />
                            Broker Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <p className="text-zinc-500 text-xs">Broker Name</p>
                            <p className="text-white font-medium text-lg">{broker.name || load.broker_name || 'Unknown'}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <p className="text-zinc-500 text-xs">Credit Score</p>
                                <Badge className={`${(broker.credit_score || load.broker_credit_score) >= 90 ? 'bg-green-500' : 'bg-red-500'}`}>
                                    {broker.credit_score || load.broker_credit_score || '--'}
                                </Badge>
                            </div>
                            <div>
                                <p className="text-zinc-500 text-xs">Days to Pay</p>
                                <Badge className={`${(broker.days_to_pay || load.broker_days_to_pay) <= 30 ? 'bg-green-500' : 'bg-amber-500'}`}>
                                    {broker.days_to_pay || load.broker_days_to_pay || '--'}d
                                </Badge>
                            </div>
                        </div>
                        <div className="pt-3 border-t border-zinc-700 space-y-2">
                            {(broker.contact_phone || broker.phone || load.broker_phone) && (
                                <a 
                                    href={`tel:${broker.contact_phone || broker.phone || load.broker_phone}`}
                                    className="flex items-center gap-2 p-2 bg-zinc-800 rounded hover:bg-zinc-700 transition-colors"
                                    data-testid="broker-phone"
                                >
                                    <Phone className="w-4 h-4 text-green-500" />
                                    <span className="text-white">{broker.contact_phone || broker.phone || load.broker_phone}</span>
                                </a>
                            )}
                            {(broker.contact_email || broker.email || load.broker_email) && (
                                <a 
                                    href={`mailto:${broker.contact_email || broker.email || load.broker_email}`}
                                    className="flex items-center gap-2 p-2 bg-zinc-800 rounded hover:bg-zinc-700 transition-colors"
                                    data-testid="broker-email"
                                >
                                    <Mail className="w-4 h-4 text-blue-500" />
                                    <span className="text-white truncate">{broker.contact_email || broker.email || load.broker_email}</span>
                                </a>
                            )}
                            {!broker.contact_phone && !broker.phone && !load.broker_phone && !broker.contact_email && !broker.email && !load.broker_email && (
                                <p className="text-zinc-500 text-sm text-center py-2">No contact info available</p>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Truck Information */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Truck className="w-4 h-4 text-orange-500" />
                            Truck Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Truck ID</span>
                            <span className="text-white font-mono">{truck.truck_id?.slice(-8) || item?.truck_id?.slice(-8)}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Driver</span>
                            <span className="text-white">{truck.driver_name || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-zinc-500">Location</span>
                            <span className="text-white">{truck.current_city || 'Unknown'}, {truck.current_state || ''}</span>
                        </div>
                        <div className="pt-3 border-t border-zinc-700 space-y-2">
                            <div className="bg-zinc-800/50 rounded p-2">
                                <p className="text-zinc-500 text-xs">ETA to Pickup</p>
                                <p className="text-white font-medium">{pickupETA.eta}</p>
                                <p className="text-zinc-400 text-xs">{pickupETA.hours}h drive</p>
                            </div>
                            <div className="bg-zinc-800/50 rounded p-2">
                                <p className="text-zinc-500 text-xs">ETA Delivery</p>
                                <p className="text-white font-medium">{deliveryETA.eta}</p>
                                <p className="text-zinc-400 text-xs">{deliveryETA.hours}h total</p>
                            </div>
                        </div>
                        {hosState && (
                            <div className="pt-2 border-t border-zinc-700">
                                <p className="text-zinc-500 text-xs mb-2">HOS Status</p>
                                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                                    <div>
                                        <p className="text-zinc-500">Drive</p>
                                        <p className="text-white font-medium">{hosState.drive_hours_remaining?.toFixed(1) || '--'}h</p>
                                    </div>
                                    <div>
                                        <p className="text-zinc-500">On-Duty</p>
                                        <p className="text-white font-medium">{hosState.on_duty_window_remaining?.toFixed(1) || '--'}h</p>
                                    </div>
                                    <div>
                                        <p className="text-zinc-500">Cycle</p>
                                        <p className="text-white font-medium">{hosState.cycle_hours_remaining?.toFixed(1) || '--'}h</p>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Safety Info Button */}
                        <div className="pt-3 border-t border-zinc-700">
                            <SafetyInfoModal 
                                truckId={truck.truck_id || item?.truck_id} 
                                truckName={truck.name || 'Truck'}
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Second Row: Negotiation + Call + Rate Confirmation */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Negotiation Assistant */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Zap className="w-4 h-4 text-amber-500" />
                            THE BRAIN: Negotiation Assistant
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <NegotiationBrain load={load} onRefresh={onRefresh} />
                    </CardContent>
                </Card>

                {/* Live Call Caption Panel */}
                <LiveCallCaptionPanel
                    loadId={load.load_id}
                    brokerPhone={broker.contact_phone || broker.phone}
                    brokerName={broker.name || load.broker_name}
                    onTranscriptSaved={onRefresh}
                />
                
                {/* Live Call Assistant - AI Negotiation Support */}
                <LiveCallAssistant 
                    loadId={load.load_id}
                    onSuggestionUsed={(suggestion) => {
                        console.log('Suggestion used:', suggestion);
                    }}
                />
            </div>

            {/* Rate Confirmation Section */}
            <RateConfirmationPanel
                item={item}
                loadId={load.load_id}
                onVerify={onVerify}
                onRefresh={onRefresh}
                verifying={verifying}
            />

            {/* High-Risk Broker Warning Banner */}
            {brokerIsHighRisk && (
                <div className="p-4 bg-red-500/10 border-2 border-red-500/50 rounded-lg" data-testid="high-risk-broker-warning">
                    <div className="flex items-start gap-3">
                        <ShieldAlert className="w-6 h-6 text-red-500 flex-shrink-0" />
                        <div>
                            <p className="text-red-400 font-semibold text-lg">⚠️ High-Risk Broker Warning</p>
                            <p className="text-zinc-300 text-sm mt-1">
                                This broker has a risk score of <strong>{brokerRiskScore}/100</strong>. 
                                Manual override is required to book this load.
                            </p>
                            <p className="text-zinc-400 text-xs mt-2">
                                Consider: {brokerRisk?.ai_insights?.recommendations?.[0] || 'Verify broker credentials before proceeding'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Book Load Button - Main Action */}
            <Card className="bg-zinc-900/50 border-zinc-800 border-2 border-purple-500/30">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <BookCheck className="w-5 h-5 text-purple-500" />
                        Book Load
                        {bookingBlockedReason && (
                            <Badge variant="outline" className="ml-2 border-amber-500/50 text-amber-400 text-xs">
                                {bookingBlockedReason}
                            </Badge>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {/* Booking Gate Warning */}
                    {!canBook && !canOverrideBook && bookingBlockedReason && (
                        <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                            <div className="flex items-center gap-2 text-amber-400 text-sm">
                                <AlertTriangle className="w-4 h-4" />
                                <span className="font-medium">Booking Blocked:</span>
                                <span>{bookingBlockedReason}</span>
                            </div>
                        </div>
                    )}
                    
                    <div className="flex items-center gap-4">
                        <Button
                            size="lg"
                            onClick={() => onBook(load.load_id, false, false)}
                            disabled={bookingLoading || !canBook}
                            className={`flex-1 h-16 text-lg font-semibold ${
                                canBook ? 'bg-green-600 hover:bg-green-700' :
                                'bg-zinc-700 cursor-not-allowed opacity-60'
                            }`}
                            data-testid="book-load-btn"
                        >
                            {bookingLoading ? (
                                <Loader2 className="w-6 h-6 animate-spin mr-2" />
                            ) : canBook ? (
                                <CheckCircle className="w-6 h-6 mr-2" />
                            ) : (
                                <Clock className="w-6 h-6 mr-2" />
                            )}
                            {canBook ? 'Book Load' :
                             !hasRateCon ? 'Upload Rate Confirmation' :
                             verificationStatus === 'pending' ? 'Run AI Verification First' :
                             'Verification Required'}
                        </Button>

                        {/* Override Button - Shows when verification failed or broker is high risk */}
                        {canOverrideBook && !isSigned && (
                            <Button
                                size="lg"
                                onClick={() => onBook(load.load_id, true, true, brokerIsHighRisk)}
                                disabled={bookingLoading}
                                className="bg-amber-600 hover:bg-amber-700 text-white h-16"
                                data-testid="override-book-btn"
                            >
                                <ShieldAlert className="w-5 h-5 mr-2" />
                                Override & Book
                            </Button>
                        )}

                        <Button
                            size="lg"
                            variant="outline"
                            onClick={() => onRemove(load.load_id)}
                            disabled={bookingLoading}
                            className="border-red-500 text-red-400 hover:bg-red-500/10 h-16"
                            data-testid="remove-load-btn"
                        >
                            <Trash2 className="w-5 h-5 mr-2" />
                            Remove
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default function OperationsPage() {
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState({});
    const [bookingLoading, setBookingLoading] = useState(false);
    const [verifying, setVerifying] = useState(false);

    const fetchQueue = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/operations/queue`, {
                credentials: 'include'
            });
            if (res.ok) {
                const data = await res.json();
                setQueue(data.queue || []);
            }
        } catch (e) {
            console.error('Failed to fetch queue:', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchQueue();
    }, [fetchQueue]);

    const handleMoveToTop = async (loadId) => {
        setActionLoading(prev => ({ ...prev, [loadId]: true }));
        try {
            const res = await fetch(`${API_URL}/api/operations/queue/${loadId}/move-to-top`, {
                method: 'POST',
                credentials: 'include'
            });
            if (res.ok) {
                toast.success('Load moved to top of queue');
                fetchQueue();
            } else {
                toast.error('Failed to move load');
            }
        } catch (e) {
            toast.error('Failed to move load');
        } finally {
            setActionLoading(prev => ({ ...prev, [loadId]: false }));
        }
    };

    const handleRemove = async (loadId) => {
        if (!window.confirm('Remove this load from the operations queue?')) return;
        
        setActionLoading(prev => ({ ...prev, [loadId]: true }));
        try {
            const res = await fetch(`${API_URL}/api/operations/queue/${loadId}`, {
                method: 'DELETE',
                credentials: 'include'
            });
            if (res.ok) {
                toast.success('Load removed from queue');
                fetchQueue();
            } else {
                toast.error('Failed to remove load');
            }
        } catch (e) {
            toast.error('Failed to remove load');
        } finally {
            setActionLoading(prev => ({ ...prev, [loadId]: false }));
        }
    };

    const handleVerify = async (loadId, rateConfirmationText, callTranscript) => {
        setVerifying(true);
        try {
            const res = await fetch(`${API_URL}/api/operations/queue/${loadId}/verify-rate-confirmation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ rate_confirmation_text: rateConfirmationText, call_transcript: callTranscript })
            });
            if (res.ok) {
                const data = await res.json();
                if (data.status === 'verified') {
                    toast.success('Rate confirmation verified!');
                } else {
                    toast.warning('Discrepancies detected - review before booking');
                }
                fetchQueue();
            } else {
                toast.error('Verification failed');
            }
        } catch (e) {
            toast.error('Verification failed');
        } finally {
            setVerifying(false);
        }
    };

    const handleBook = async (loadId, overrideDiscrepancy, overrideVerification = false, overrideBrokerRisk = false) => {
        setBookingLoading(true);
        try {
            const params = new URLSearchParams({
                override_discrepancy: overrideDiscrepancy,
                override_verification: overrideVerification,
                override_broker_risk: overrideBrokerRisk
            });
            
            const res = await fetch(`${API_URL}/api/operations/queue/${loadId}/book?${params}`, {
                method: 'POST',
                credentials: 'include'
            });
            if (res.ok) {
                toast.success('Load booked successfully!', {
                    description: 'Load is now in My Loads and Truck Monitoring'
                });
                fetchQueue();
            } else {
                const error = await res.json();
                // Show detailed booking gate error
                if (error.detail?.includes('BOOKING BLOCKED')) {
                    toast.error('Booking Blocked', { 
                        description: error.detail.replace('BOOKING BLOCKED: ', ''),
                        duration: 6000
                    });
                } else {
                    toast.error('Booking failed', { description: error.detail || 'Unknown error' });
                }
            }
        } catch (e) {
            toast.error('Booking failed');
        } finally {
            setBookingLoading(false);
        }
    };

    const activeLoad = queue.find(item => item.position === 0);
    const waitingLoads = queue.filter(item => item.position > 0);

    return (
        <div className="p-6 space-y-6" data-testid="operations-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                        <Briefcase className="w-7 h-7 text-purple-500" />
                        Operations
                    </h1>
                    <p className="text-zinc-400 text-sm mt-1">
                        Dispatcher workflow center - negotiate, verify, and book loads
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <Badge variant="outline" className="border-purple-500 text-purple-400">
                        {queue.length}/5 in queue
                    </Badge>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchQueue}
                        disabled={loading}
                        className="border-zinc-700 text-zinc-300"
                        data-testid="refresh-queue"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                </div>
            ) : queue.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="py-20 text-center">
                        <Briefcase className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
                        <h2 className="text-xl font-semibold text-white mb-2">No Loads in Queue</h2>
                        <p className="text-zinc-400 mb-4">
                            Stage loads from the Command Center to begin the negotiation workflow
                        </p>
                        <Button
                            variant="outline"
                            className="border-purple-500 text-purple-400"
                            onClick={() => window.location.href = '/command-center'}
                        >
                            Go to Command Center
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Active Load (Position 0) */}
                    {activeLoad && (
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <Badge className="bg-purple-500">Active</Badge>
                                <h2 className="text-lg font-semibold text-white">Current Load</h2>
                            </div>
                            <ActiveLoadPanel
                                item={activeLoad}
                                onBook={handleBook}
                                onRemove={handleRemove}
                                onVerify={handleVerify}
                                onRefresh={fetchQueue}
                                bookingLoading={bookingLoading}
                                verifying={verifying}
                            />
                        </div>
                    )}

                    {/* Waiting Loads */}
                    {waitingLoads.length > 0 && (
                        <div>
                            <h2 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                <Clock className="w-5 h-5 text-zinc-500" />
                                Waiting ({waitingLoads.length})
                            </h2>
                            <div className="space-y-2">
                                {waitingLoads.map(item => (
                                    <WaitingLoadCard
                                        key={item.load_id}
                                        item={item}
                                        onMoveToTop={handleMoveToTop}
                                        onRemove={handleRemove}
                                        loading={actionLoading[item.load_id]}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
